import React, { useState, useRef, useEffect } from 'react';
import { B, FONT, fmt$, fmtDate, LOGO_FALLBACK } from '../lib/constants';
import { useDailyLog, useTodos, useDecisions } from '../hooks/useData';
import { LogEntryModal } from './modals/LogPickerModal';
import AddTaskModal from './modals/AddTaskModal';
import AddDecisionModal from './modals/AddDecisionModal';
import DecisionDetailModal from './modals/DecisionDetailModal';
import WeatherWidget from './WeatherWidget';
import { ensureToken } from '../lib/google';
import { supabase } from '../lib/supabase';
import PhaseTab from './PhaseTab';
import PurchasesTab from './PurchasesTab';
import ChecklistTab from './ChecklistTab';

const TABS = ['overview', 'alerts', 'media', 'todos', 'decisions', 'purchases', 'checklist', 'notes', 'log', 'financial', 'phases'];
const TAB_LABELS = { overview: 'Overview', alerts: 'Notifications', alerts: 'Notifications', media: 'Media', todos: 'Tasks', decisions: 'Decisions', purchases: 'Purchases', checklist: 'Checklist', notes: 'Notes', log: 'Log', financial: 'Financial', phases: 'Phases' };
const CATEGORIES = [
  'Exterior', 'Interior', 'Foundation', 'Concrete', 'Framing', 'Fireplaces', 'Windows/Doors',
  'Plumbing', 'Electrical', 'HVAC', 'Tile', 'Flooring', 'Trim', 'Paint/Stain', 'Appliances', 'Finishes',
];


// Android back button closes the topmost open box instead of leaving the page
function useBackToClose(open, close) {
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ mhModal: true }, '');
    const onPop = () => close();
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Closing via a button: neutralize the marker without navigating anywhere
      if (window.history.state && window.history.state.mhModal) window.history.replaceState({}, '');
    };
  }, [open]);
}

export default function ProjectDetail({ project, allProjects, tab, setTab, onGoHome, onGoBack, hideBackButton, onUpdateProject, onRefresh }) {
  const [todoView, setTodoView] = useState('open');
  const [showLogModal, setShowLogModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState(null);

  useBackToClose(showLogModal, () => setShowLogModal(false));
  useBackToClose(showTaskModal, () => setShowTaskModal(false));
  useBackToClose(showPhotoModal, () => setShowPhotoModal(false));
  useBackToClose(showDecisionModal, () => setShowDecisionModal(false));
  useBackToClose(!!selectedDecision, () => setSelectedDecision(null));

  const { logs, refetch: refetchLogs } = useDailyLog(project.id);
  const { todos, toggleTodo, addTodo, reorderTodos, refetch: refetchTodos } = useTodos(project.id);
  const { decisions, updateDecision, refetch: refetchDecisions } = useDecisions(project.id);

  const pct = project.estimate > 0 ? Math.round((project.spent / project.estimate) * 100) : 0;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const recentLogs = (logs || []).filter(e => new Date(e.log_date + 'T12:00:00') >= sevenDaysAgo);

  return (
    <div style={{ minHeight: '100vh', background: B.black, fontFamily: FONT, display: 'flex', flexDirection: 'column' }}>

      <div style={{ background: B.dark, borderBottom: `1px solid ${B.slate}`, padding: '12px 16px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          {!hideBackButton && (
            <button onClick={onGoBack || onGoHome} style={{ background: 'none', border: 'none', color: B.muted, cursor: 'pointer', fontSize: 20, padding: 0, lineHeight: 1 }}>←</button>
          )}
          <img
            src={LOGO_FALLBACK}
            alt="Home"
            onClick={onGoHome}
            onError={e => e.target.style.display = 'none'}
            style={{ height: 30, cursor: 'pointer', flexShrink: 0, borderRadius: 6 }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase' }}>{project.phase} · {project.progress}%</div>
            <div style={{ color: B.white, fontSize: 18, fontWeight: 900 }}>{project.name}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {tab === 'decisions' ? (
              <button onClick={() => setShowDecisionModal(true)} style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: FONT }}>+ Decision</button>
            ) : tab === 'todos' ? (
              <button onClick={() => setShowTaskModal(true)} style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: FONT }}>+ Task</button>
            ) : tab === 'media' ? (
              <button onClick={() => setShowPhotoModal(true)} style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: FONT }}>+ Photo</button>
            ) : (
              <button onClick={() => setShowLogModal(true)} style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: FONT }}>+ Log</button>
            )}
          </div>
        </div>
      </div>

      <div style={{ background: B.dark, borderBottom: `1px solid ${B.slate}`, display: 'flex', overflowX: 'auto', flexShrink: 0, scrollbarWidth: 'none' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ background: 'transparent', color: tab === t ? B.goldlt : B.muted, border: 'none', borderBottom: tab === t ? `2px solid ${B.gold}` : '2px solid #6F6F6F', padding: '11px 14px', cursor: 'pointer', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: FONT, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {TAB_LABELS[t] || t}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>

        {tab === 'overview' && (
          <div>
            <div style={{ background: B.navy, borderRadius: 14, padding: 18, marginBottom: 12, border: `1px solid ${B.slate}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 4 }}>Progress</div>
                  <div style={{ color: B.white, fontSize: 38, fontWeight: 900, lineHeight: 1 }}>{project.progress}<span style={{ fontSize: 18 }}>%</span></div>
                  <div style={{ color: B.muted, fontSize: 12, marginTop: 4 }}>{project.phase}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: B.muted, fontSize: 10 }}>STARTED</div>
                  <div style={{ color: B.white, fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{fmtDate(project.start_date)}</div>
                  <div style={{ color: B.muted, fontSize: 10 }}>EST. COMPLETE</div>
                  <div style={{ color: B.white, fontSize: 12, fontWeight: 600 }}>{fmtDate(project.est_complete)}</div>
                </div>
              </div>
              <div style={{ background: '#6F6F6F', borderRadius: 999, height: 8, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${project.progress}%`, background: `linear-gradient(90deg,${B.goldlt},${B.gold})`, borderRadius: 999 }} />
              </div>
            </div>
            <WeatherWidget address={project.address} projectId={project.id} />

            {/* TASKS */}
            <div style={{ background: B.navy, borderRadius: 14, padding: 18, marginBottom: 12, border: `1px solid ${B.slate}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div onClick={() => setTab('todos')} style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', cursor: 'pointer' }}>Tasks ›</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ color: B.muted, fontSize: 11 }}>{todos.filter(t => !t.done).length} open</div>
                  <button onClick={() => setShowTaskModal(true)} style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 6, width: 22, height: 22, cursor: 'pointer', fontSize: 15, fontWeight: 700, fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                </div>
              </div>
              {todos.filter(t => !t.done).length === 0 ? (
                <div style={{ color: B.muted, fontSize: 12, textAlign: 'center', padding: '8px 0' }}>All caught up ✅</div>
              ) : todos.filter(t => !t.done).slice(0, 5).map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8, paddingBottom: 8, borderBottom: `1px solid ${B.slate}` }}>
                  <button onClick={() => toggleTodo(t.id, true)} style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${B.gold}`, background: 'transparent', cursor: 'pointer', flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1 }} onClick={() => setTab('todos')}>
                    <div style={{ color: B.white, fontSize: 13, lineHeight: 1.4 }}>{t.text}</div>
                    <div style={{ color: t.priority === 'high' ? B.goldlt : '#6F6F6F', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{t.priority}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* NOTES */}
            <OverviewNotesCard projectId={project.id} onOpen={() => setTab('notes')} />

            {/* DECISIONS */}
            {(() => {
              const pendingDecs = decisions.filter(d => d.status === 'pending');
              const overdueDecs = pendingDecs.filter(d => d.due_date && new Date(d.due_date + 'T12:00:00') < new Date());
              return (
                <div style={{ background: B.navy, borderRadius: 14, padding: 18, marginBottom: 12, border: `1px solid ${overdueDecs.length > 0 ? '#E74C3C' : pendingDecs.length > 0 ? B.gold : B.slate}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: pendingDecs.length > 0 ? 8 : 0 }}>
                    <div onClick={() => setTab('decisions')} style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', cursor: 'pointer' }}>Decisions ›</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ color: B.muted, fontSize: 11 }}>{pendingDecs.length} pending</div>
                      <button onClick={() => setShowDecisionModal(true)} style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 6, width: 22, height: 22, cursor: 'pointer', fontSize: 15, fontWeight: 700, fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                    </div>
                  </div>
                  {pendingDecs.slice(0, 3).map(d => {
                    const isOverdue = d.due_date && new Date(d.due_date + 'T12:00:00') < new Date();
                    return (
                      <div key={d.id} onClick={() => setTab('decisions')} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 6, marginBottom: 6, borderBottom: `1px solid ${B.slate}`, cursor: 'pointer' }}>
                        <div style={{ color: B.white, fontSize: 13 }}>{d.item}</div>
                        {d.due_date && <div style={{ color: isOverdue ? '#E74C3C' : B.muted, fontSize: 11, flexShrink: 0, marginLeft: 8 }}>{isOverdue ? 'Past Due' : fmtDate(d.due_date)}</div>}
                      </div>
                    );
                  })}
                  {pendingDecs.length > 3 && <div style={{ color: B.muted, fontSize: 11, marginTop: 4 }}>+{pendingDecs.length - 3} more</div>}
                </div>
              );
            })()}

            {/* PURCHASES */}
            <OverviewPurchasesCard projectId={project.id} onOpen={() => setTab('purchases')} />

            {/* LOG */}
            <div style={{ background: B.navy, borderRadius: 14, padding: 18, marginBottom: 12, border: `1px solid ${B.slate}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div onClick={() => setTab('log')} style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', cursor: 'pointer' }}>Log — Last 7 Days ›</div>
                <button onClick={() => setShowLogModal(true)} style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 6, width: 22, height: 22, cursor: 'pointer', fontSize: 15, fontWeight: 700, fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
              </div>
              {recentLogs.length === 0 ? (
                <div style={{ color: B.muted, fontSize: 12, textAlign: 'center', padding: '8px 0' }}>No entries in the last 7 days.</div>
              ) : recentLogs.map(e => (
                <div key={e.id} onClick={() => setTab('log')} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${B.slate}`, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ color: B.white, fontSize: 13, fontWeight: 700 }}>{fmtDate(e.log_date)}</div>
                    {e.weather && <div style={{ color: B.goldlt, fontSize: 12 }}>{e.weather}</div>}
                  </div>
                  {e.trades?.length > 0 && <div style={{ marginBottom: 4 }}>{e.trades.map(t => <span key={t} style={{ background: '#6F6F6F', color: B.white, borderRadius: 4, padding: '2px 6px', fontSize: 10, marginRight: 3 }}>{t}</span>)}</div>}
                  <div style={{ color: B.light, fontSize: 12, lineHeight: 1.5 }}>{e.notes}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'todos' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ color: B.white, fontSize: 18, fontWeight: 700 }}>Tasks</div>
              <button onClick={() => setShowTaskModal(true)} style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 9, padding: '8px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: FONT }}>+ Add Task</button>
            </div>
            <div style={{ display: 'flex', gap: 0, marginBottom: 14, background: B.slate, borderRadius: 9, padding: 3 }}>
              {['open', 'completed'].map(v => (
                <button key={v} onClick={() => setTodoView(v)} style={{ flex: 1, background: todoView === v ? B.goldlt : 'transparent', color: todoView === v ? B.black : B.muted, border: 'none', borderRadius: 7, padding: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, textTransform: 'capitalize', fontFamily: FONT }}>
                  {v === 'open' ? `Open (${todos.filter(t => !t.done).length})` : `Done (${todos.filter(t => t.done).length})`}
                </button>
              ))}
            </div>
            {todoView === 'open' && (
              todos.filter(t => !t.done).length === 0 ? (
                <div style={{ background: B.navy, borderRadius: 14, padding: 48, textAlign: 'center', border: `1px solid ${B.slate}` }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>✅</div>
                  <div style={{ color: B.white, fontSize: 16, fontWeight: 700 }}>All caught up</div>
                </div>
              ) : (
                <SortableTasksList
                  tasks={todos.filter(t => !t.done)}
                  onComplete={(id) => toggleTodo(id, true)}
                  onUpdate={refetchTodos}
                  onReorder={reorderTodos}
                />
              )
            )}
            {todoView === 'completed' && (
              todos.filter(t => t.done).map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: B.dark, borderRadius: 11, padding: '12px 16px', marginBottom: 8, border: `1px solid ${B.slate}` }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#4CAF7D', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: B.white, fontSize: 10, fontWeight: 700 }}>✓</span>
                  </div>
                  <div style={{ flex: 1, color: B.muted, fontSize: 13, textDecoration: 'line-through' }}>{t.text}</div>
                  <button onClick={() => toggleTodo(t.id, false)} style={{ background: 'none', border: `1px solid ${B.slate}`, borderRadius: 5, color: B.muted, cursor: 'pointer', fontSize: 10, fontWeight: 700, padding: '2px 8px', fontFamily: FONT }}>Reopen</button>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'log' && (
          <LogTab logs={logs} projectId={project.id} onNewEntry={() => setShowLogModal(true)} onRefresh={refetchLogs} />
        )}

        {tab === 'decisions' && (
          <DecisionsTab decisions={decisions} projectId={project.id} onSelect={setSelectedDecision} onAdd={() => setShowDecisionModal(true)} onReload={refetchDecisions} />
        )}

        <div style={{ display: tab === 'financial' ? 'block' : 'none' }}>
          <FinancialTab project={project} />
        </div>

        {tab === 'purchases' && (
          <PurchasesTab project={project} isBuilder={true} />
        )}

        {tab === 'notes' && (
          <BuilderNotesTab project={project} />
        )}

        {tab === 'checklist' && (
          <ChecklistTab project={project} isBuilder={true} />
        )}

        {tab === 'alerts' && (
          <NotificationsTab projectId={project.id} role="builder" />
        )}

        {tab === 'phases' && (
          <PhaseTab project={project} onRefresh={onRefresh} />
        )}

        {tab === 'media' && (
          <MediaTab project={project} onAddPhoto={() => setShowPhotoModal(true)} />
        )}

      </div>

      {showLogModal && <LogEntryModal projectId={project.id} onClose={() => setShowLogModal(false)} onSuccess={refetchLogs} />}
      {showTaskModal && <AddTaskModal projects={allProjects} defaultProjectId={project.id} onClose={() => setShowTaskModal(false)} onSuccess={refetchTodos} />}
      {showDecisionModal && <AddDecisionModal projects={allProjects || [project]} defaultProjectId={project.id} onClose={() => setShowDecisionModal(false)} onSuccess={() => { setShowDecisionModal(false); refetchDecisions(); }} />}
      {selectedDecision && <DecisionDetailModal decision={selectedDecision} isBuilder={true} onClose={() => setSelectedDecision(null)} onSuccess={() => { setSelectedDecision(null); refetchDecisions(); }} />}
    </div>
  );
}

const DRIVE_MEDIA_FOLDERS = { 1: '1Ww2LuFXi8PxV4pVMpoIQekhzA0Jz2FYN', 2: '1oRoS-NXvBdyQkru3hVLe7i7HHLy8X7o5', 3: '1PH2p8eivHFJLv3Y-DcAMy3TOiov5nt2H', 4: '130B7I0_sLP2vcggXBIcuhxX24XR_aMLa' };

function MediaTab({ project, onAddPhoto }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState('');
  const [selected, setSelected] = useState(null);
  useBackToClose(!!selected, () => setSelected(null));

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErrMsg('');
    const token = localStorage.getItem('google_access_token');
    const folderId = DRIVE_MEDIA_FOLDERS[parseInt(project.id)];
    if (!token) { setErrMsg('Connect Google to view photos.'); setLoading(false); return; }
    if (!folderId) { setErrMsg('No folder configured.'); setLoading(false); return; }
    const fetchAll = async () => {
      try {
        const r = await fetch(`https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent("'" + folderId + "' in parents and trashed=false")}&fields=files(id,name,mimeType,webViewLink,createdTime)&orderBy=createdTime%20desc&pageSize=200`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json();
        const top = d.files || [];
        const folders = top.filter(f => f.mimeType === 'application/vnd.google-apps.folder');
        const direct = top.filter(f => f.mimeType && (f.mimeType.startsWith('image/') || f.mimeType.startsWith('video/')));
        const subResults = await Promise.all(folders.map(async fld => {
          try {
            const sr = await fetch(`https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent("'" + fld.id + "' in parents and trashed=false")}&fields=files(id,name,mimeType,webViewLink,createdTime)&orderBy=createdTime%20desc&pageSize=200`, { headers: { Authorization: `Bearer ${token}` } });
            const sd = await sr.json();
            return (sd.files || []).filter(f => f.mimeType && (f.mimeType.startsWith('image/') || f.mimeType.startsWith('video/')));
          } catch(e) { return []; }
        }));
        if (!cancelled) { setFiles([...direct, ...subResults.flat()].sort((a,b) => new Date(b.createdTime) - new Date(a.createdTime))); setLoading(false); }
      } catch(e) { if (!cancelled) { setErrMsg('Failed to load.'); setLoading(false); } }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [project.id]);

  if (loading) return <div style={{padding:40,textAlign:'center',color:B.muted,fontSize:13}}>Loading photos…</div>;
  if (errMsg) return <div style={{background:B.navy,borderRadius:14,padding:30,textAlign:'center',border:`1px solid ${B.slate}`}}><div style={{fontSize:32,marginBottom:10}}>📷</div><div style={{color:B.muted,fontSize:13}}>{errMsg}</div></div>;

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div style={{color:B.white,fontSize:18,fontWeight:700}}>Media <span style={{color:B.muted,fontSize:13,fontWeight:400}}>({files.length})</span></div>
        <button onClick={onAddPhoto} style={{background:B.goldlt,color:B.black,border:'none',borderRadius:8,padding:'7px 14px',cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:FONT}}>📷 Add</button>
      </div>
      {files.length === 0 ? (
        <div style={{background:B.navy,borderRadius:14,padding:40,textAlign:'center',border:`1px solid ${B.slate}`}}>
          <div style={{fontSize:40,marginBottom:12}}>📷</div>
          <div style={{color:B.muted,fontSize:13}}>No photos yet.</div>
        </div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:3}}>
          {files.map(f => (
            <div key={f.id} onClick={() => setSelected(f)} style={{aspectRatio:'1',overflow:'hidden',borderRadius:6,cursor:'pointer',background:B.slate}}>
              <img src={`https://drive.google.com/thumbnail?id=${f.id}&sz=w400`} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} onError={e => { e.target.style.display='none'; }} />
            </div>
          ))}
        </div>
      )}
      {selected && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.96)',zIndex:2000,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}} onClick={() => setSelected(null)}>
          <div style={{position:'absolute',top:16,right:16,display:'flex',gap:10}} onClick={e => e.stopPropagation()}>
            <a href={selected.webViewLink} target="_blank" rel="noreferrer" style={{background:B.goldlt,color:'#000',borderRadius:8,padding:'8px 14px',textDecoration:'none',fontSize:12,fontWeight:700,fontFamily:FONT}}>Open in Drive ↗</a>
            <button onClick={() => setSelected(null)} style={{background:'rgba(255,255,255,0.1)',border:'none',borderRadius:8,padding:'8px 14px',cursor:'pointer',color:'#fff',fontSize:20}}>×</button>
          </div>
          <img src={`https://drive.google.com/thumbnail?id=${selected.id}&sz=w1200`} alt="" style={{maxWidth:'95vw',maxHeight:'80vh',objectFit:'contain',borderRadius:8}} onClick={e => e.stopPropagation()} onError={e => { e.target.src = `https://drive.google.com/thumbnail?id=${selected.id}&sz=w400`; }} />
          <div style={{color:'#fff',fontSize:12,marginTop:12,opacity:0.6}}>{selected.name}</div>
        </div>
      )}
    </div>
  );
}

const FINANCIAL_SHEET_IDS = { 1: '10XOOYS3yaXsmwf4wk5BHOXO1Y6LJ5c5qKMPzfAZShJ0', 2: '1TeirSGjgSZalSDpyxxAHFImBZmmzCD7xeU99Hb4rAck', 3: '12aZNIwtf4z8jveRGFyWRfHlqwvIe4lCGrPfj0RUHCcs', 4: '1Q-GKQn4KMssGCRIg9o2AnFUT6CcyKQ5GVFd4-lWP-FE' };

function FinancialTab({ project }) {
  const [finTab, setFinTab] = useState('summary');
  const [cats, setCats] = useState(() => { try { return JSON.parse(localStorage.getItem(`fin_cats_${project.id}`) || '[]'); } catch { return []; } });
  const [exps, setExps] = useState(() => { try { return JSON.parse(localStorage.getItem(`fin_exps_${project.id}`) || '[]'); } catch { return []; } });
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const totalEstimate = cats.length > 0 ? cats.reduce((s, c) => s + c.estimate, 0) : project.estimate;
  const totalActual = cats.length > 0 ? cats.reduce((s, c) => s + c.actual, 0) : project.spent;
  const pct = totalEstimate > 0 ? Math.round((totalActual / totalEstimate) * 100) : 0;

  const doSync = async () => {
    const sheetId = FINANCIAL_SHEET_IDS[parseInt(project.id)];
    if (!sheetId) return;
    setSyncing(true);
    try {
      const token = await ensureToken();
      if (!token) { setSyncing(false); return; }
      const cleanNum = (val) => {
        if (!val) return 0;
        const n = parseFloat(('' + val).replace(/[^0-9.-]+/g, ''));
        return isNaN(n) ? 0 : n;
      };

      // ---- Budget vs. Actuals ----
      const res = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/Budget%20vs.%20Actuals!A:M?majorDimension=ROWS`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      const allRows = data.values || [];

      // Find the REAL header row — must contain BOTH "Budget Category" and "Line Item"
      // (this skips the "Project Running Totals" block that also says "Estimated Amount")
      let headerIdx = -1;
      let catCol = 2, itemCol = 3, estCol = 6, updCol = 7, actCol = 8;
      for (let i = 0; i < Math.min(30, allRows.length); i++) {
        const row = allRows[i] || [];
        const joined = row.join(' ').toLowerCase();
        if (joined.includes('budget category') && joined.includes('line item')) {
          headerIdx = i;
          row.forEach((c, idx) => {
            const cell = ('' + (c || '')).toLowerCase().replace(/\s+/g, ' ').trim();
            if (cell.includes('budget category')) catCol = idx;
            if (cell === 'line item') itemCol = idx;
            if (cell.includes('original estimated')) estCol = idx;
            if (cell.includes('updated estimated')) updCol = idx;
            if (cell.includes('actuals to date')) actCol = idx;
          });
          break;
        }
      }

      if (headerIdx >= 0) {
        const rows = allRows.slice(headerIdx + 1);
        const parsed = rows.map(r => {
          const catName = ('' + (r[catCol] || '')).trim();
          const itemName = ('' + (r[itemCol] || '')).trim();
          if (!catName && !itemName) return null;
          const lower = (catName + ' ' + itemName).toLowerCase();
          if (lower.includes('total') || lower.includes('project running')) return null;
          const est = cleanNum(r[estCol]);
          const upd = cleanNum(r[updCol]);
          const act = cleanNum(r[actCol]);
          if (est === 0 && upd === 0 && act === 0) return null;
          return { category: itemName || catName, estimate: est || upd, updated: upd || est, actual: act };
        }).filter(Boolean);

        if (parsed.length > 0) {
          setCats(parsed);
          localStorage.setItem(`fin_cats_${project.id}`, JSON.stringify(parsed));
          // Write totals to the database so the dashboard + client portal stay accurate
          const totEst = parsed.reduce((s, c) => s + (c.updated || c.estimate), 0);
          const totAct = parsed.reduce((s, c) => s + c.actual, 0);
          if (totEst > 0) {
            await supabase.from('projects').update({
              estimate: Math.round(totEst),
              spent: Math.round(totAct),
            }).eq('id', project.id);
          }
          // Mirror line items to Supabase so the client portal shows the full budget
          try {
            await supabase.from('project_financial_lines').delete().eq('project_id', project.id);
            await supabase.from('project_financial_lines').insert(parsed.map((c, i) => ({
              project_id: parseInt(project.id),
              category: c.category,
              estimate: c.estimate,
              updated: c.updated,
              actual: c.actual,
              sort: i,
            })));
          } catch(e) { console.error('Line mirror failed:', e); }
        }
      }

      // ---- Expense Log ----
      const expRes = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}?includeGridData=true&ranges=Expense%20Log!A:L`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const expData = await expRes.json();
      if (!expRes.ok || expData.error) {
        console.error('EXPENSE FETCH FAILED:', expRes.status, JSON.stringify(expData.error || expData).slice(0, 300));
      }
      // Google splits large sheets into multiple grid chunks — read ALL of them
      const expRowData = (expData.sheets?.[0]?.data || []).flatMap(g => g.rowData || []);
      console.log('EXPENSE ROWS FETCHED:', expRowData.length);

      // Require the header row before parsing — no defaults-guessing
      let expHeaderIdx = -1;
      let expAmtCol = -1, expLinkCol = -1, expVendorCol = -1, expDescCol = -1, expPaidByCol = -1, expDateCol = -1, expCatCol = -1;
      for (let i = 0; i < Math.min(15, expRowData.length); i++) {
        const cells = (expRowData[i]?.values || []).map(c => (c?.formattedValue || '').toLowerCase().replace(/\s+/g, ' ').trim());
        const joined = cells.join(' ');
        if (joined.includes('amount') && joined.includes('vendor')) {
          expHeaderIdx = i;
          cells.forEach((c, idx) => {
            if (c === 'amount') expAmtCol = idx;
            if (c === 'vendor') expVendorCol = idx;
            if (c.includes('transaction information') || c === 'description') expDescCol = idx;
            if (c.includes('client/builder paid') || c.includes('paid by')) expPaidByCol = idx;
            if (c.includes('transaction date') || c === 'date') expDateCol = idx;
            if (c.includes('receipt') || c.includes('invoice')) expLinkCol = idx;
            if (c.includes('budget line item') || c === 'category') expCatCol = idx;
          });
          break;
        }
      }

      if (expHeaderIdx < 0 || expAmtCol < 0) {
        console.error('EXPENSE HEADER NOT FOUND — headerIdx:', expHeaderIdx, 'amountCol:', expAmtCol);
      }
      if (expHeaderIdx >= 0 && expAmtCol >= 0) {
        const parsedExp = expRowData.slice(expHeaderIdx + 1).map((row, i) => {
          const cells = row.values || [];
          const amount = cleanNum(cells[expAmtCol]?.formattedValue);
          if (amount <= 0) return null;
          return {
            id: i,
            vendor: expVendorCol >= 0 ? (cells[expVendorCol]?.formattedValue || '') : '',
            description: expDescCol >= 0 ? (cells[expDescCol]?.formattedValue || '') : '',
            receiptLink: expLinkCol >= 0 ? (cells[expLinkCol]?.hyperlink || null) : null,
            paid_by: expPaidByCol >= 0 ? (cells[expPaidByCol]?.formattedValue || '') : '',
            expense_date: expDateCol >= 0 ? (cells[expDateCol]?.formattedValue || '') : '',
            amount,
            category: expCatCol >= 0 ? (cells[expCatCol]?.formattedValue || '') : '',
          };
        }).filter(Boolean).reverse();

        setExps(parsedExp);
        localStorage.setItem(`fin_exps_${project.id}`, JSON.stringify(parsedExp));
        // Mirror expenses to Supabase for the client portal
        try {
          await supabase.from('project_financial_expenses').delete().eq('project_id', project.id);
          await supabase.from('project_financial_expenses').insert(parsedExp.map((e, i) => ({
            project_id: parseInt(project.id),
            vendor: e.vendor || null,
            description: e.description || null,
            amount: e.amount,
            paid_by: e.paid_by || null,
            expense_date: e.expense_date || null,
            category: e.category || null,
            receipt_link: e.receiptLink || null,
            sort: i,
          })));
        } catch(err) { console.error('Expense mirror failed:', err); }
      }

      setLastSync(new Date().toLocaleTimeString());
    } catch(e) { console.error('Sheet sync error:', e.message, e.stack); }
    setSyncing(false);
  };

  // Reload cached data AND re-sync every time the project changes
  useEffect(() => {
    try { setCats(JSON.parse(localStorage.getItem(`fin_cats_${project.id}`) || '[]')); } catch { setCats([]); }
    try { setExps(JSON.parse(localStorage.getItem(`fin_exps_${project.id}`) || '[]')); } catch { setExps([]); }
    setLastSync(null);
    doSync();
  }, [project.id]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ color: B.white, fontSize: 18, fontWeight: 700 }}>Financials</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastSync && <div style={{ color: B.muted, fontSize: 10 }}>Synced {lastSync}</div>}
          <button onClick={doSync} disabled={syncing} style={{ background: syncing ? B.slate : B.gold, color: syncing ? B.muted : '#000', border: 'none', borderRadius: 7, padding: '6px 12px', cursor: syncing ? 'default' : 'pointer', fontSize: 11, fontWeight: 700, fontFamily: FONT }}>{syncing ? '…' : '↻'}</button>
          <div style={{ display: 'flex', gap: 3, background: B.slate, borderRadius: 9, padding: 3 }}>
            {['summary', 'budget vs actual', 'expenses'].map(t => (
              <button key={t} onClick={() => setFinTab(t)} style={{ background: finTab === t ? B.goldlt : 'transparent', color: finTab === t ? B.black : B.muted, border: 'none', borderRadius: 7, padding: '6px 8px', cursor: 'pointer', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', fontFamily: FONT, whiteSpace: 'nowrap' }}>
                {t === 'budget vs actual' ? 'Budget' : t === 'expenses' ? 'Expenses' : 'Summary'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {finTab === 'summary' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            {[
              { l: 'Estimate', v: fmt$(totalEstimate), c: B.white },
              { l: 'Spent', v: fmt$(totalActual), c: B.goldlt },
              { l: 'Remaining', v: fmt$(totalEstimate - totalActual), c: totalEstimate - totalActual >= 0 ? '#4CAF7D' : '#C0392B' },
              { l: 'Used', v: Math.round((totalActual / (totalEstimate || 1)) * 100) + '%', c: pct > 90 ? '#C0392B' : pct > 70 ? B.goldlt : B.white },
            ].map(s => (
              <div key={s.l} style={{ background: B.navy, borderRadius: 11, padding: 16, border: `1px solid ${B.slate}` }}>
                <div style={{ color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>{s.l}</div>
                <div style={{ color: s.c, fontSize: 16, fontWeight: 900 }}>{s.v}</div>
              </div>
            ))}
          </div>
          <div style={{ background: B.navy, borderRadius: 11, padding: 16, border: `1px solid ${B.slate}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ color: B.muted, fontSize: 12 }}>Budget Consumed</div>
              <div style={{ color: B.white, fontSize: 12, fontWeight: 700 }}>{pct}%</div>
            </div>
            <div style={{ background: '#6F6F6F', borderRadius: 999, height: 10, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: pct > 90 ? '#C0392B' : `linear-gradient(90deg,${B.goldlt},${B.gold})`, borderRadius: 999 }} />
            </div>
          </div>
          {cats.length === 0 && !syncing && <div style={{ color: B.muted, fontSize: 12, textAlign: 'center', marginTop: 12 }}>Tap ↻ to sync from your spreadsheet</div>}
        </div>
      )}

      {finTab === 'budget vs actual' && (
        <div style={{ background: B.navy, borderRadius: 12, border: `1px solid ${B.slate}`, overflow: 'hidden' }}>
          <div style={{ display: 'flex', padding: '8px 12px', background: B.slate }}>
            <div style={{ flex: 1, color: B.light, fontSize: 9, fontWeight: 700, letterSpacing: 0.5 }}>LINE ITEM</div>
            <div style={{ width: 72, color: B.light, fontSize: 9, fontWeight: 700, textAlign: 'right' }}>EST</div>
            <div style={{ width: 72, color: B.light, fontSize: 9, fontWeight: 700, textAlign: 'right' }}>ACTUAL</div>
            <div style={{ width: 60, color: B.light, fontSize: 9, fontWeight: 700, textAlign: 'right' }}>VAR</div>
          </div>
          <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
            {cats.map((c, i) => {
              const variance = c.estimate - c.actual;
              const over = c.actual > c.estimate && c.estimate > 0;
              return (
                <div key={i} style={{ display: 'flex', padding: '9px 12px', background: i % 2 === 0 ? B.navy : B.dark, borderBottom: `1px solid ${B.slate}` }}>
                  <div style={{ flex: 1, color: B.light, fontSize: 11, paddingRight: 6, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.category}</div>
                  <div style={{ width: 72, color: B.muted, fontSize: 11, textAlign: 'right', flexShrink: 0 }}>{c.estimate > 0 ? fmt$(c.estimate) : '–'}</div>
                  <div style={{ width: 72, color: c.actual > 0 ? B.white : B.muted, fontSize: 11, textAlign: 'right', fontWeight: c.actual > 0 ? 600 : 400, flexShrink: 0 }}>{c.actual > 0 ? fmt$(c.actual) : '–'}</div>
                  <div style={{ width: 60, color: c.actual === 0 ? B.muted : over ? '#C0392B' : '#4CAF7D', fontSize: 10, textAlign: 'right', fontWeight: 600, flexShrink: 0 }}>
                    {c.actual === 0 ? '–' : over ? `▲${fmt$(Math.abs(variance))}` : `▼${fmt$(Math.abs(variance))}`}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', padding: '10px 12px', background: B.mid, borderTop: `2px solid ${B.gold}` }}>
            <div style={{ flex: 1, color: B.goldlt, fontSize: 11, fontWeight: 700 }}>TOTAL</div>
            <div style={{ width: 72, color: B.goldlt, fontSize: 11, fontWeight: 700, textAlign: 'right' }}>{fmt$(totalEstimate)}</div>
            <div style={{ width: 72, color: B.goldlt, fontSize: 11, fontWeight: 700, textAlign: 'right' }}>{fmt$(totalActual)}</div>
            <div style={{ width: 60, color: totalEstimate - totalActual >= 0 ? '#4CAF7D' : '#C0392B', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>{fmt$(Math.abs(totalEstimate - totalActual))}</div>
          </div>
        </div>
      )}

      {finTab === 'expenses' && (
        <div>
          <div style={{ background: B.slate, borderRadius: 9, padding: '8px 14px', marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ color: B.muted, fontSize: 12 }}>{exps.length} transactions</div>
            <div style={{ color: B.goldlt, fontSize: 13, fontWeight: 700 }}>{fmt$(exps.reduce((s, e) => s + parseFloat(e.amount || 0), 0))}</div>
          </div>
          {exps.map((e, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, padding: '12px', background: B.navy, borderBottom: `1px solid ${B.slate}` }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ color: B.white, fontSize: 14, fontWeight: 700 }}>{e.vendor}</div>
                {e.description && <div style={{ color: B.muted, fontSize: 12 }}>{e.description}</div>}
                {e.expense_date && <div style={{ color: B.muted, fontSize: 11 }}>{e.expense_date}</div>}
                {e.receiptLink && (
                  <div onClick={(event) => { event.preventDefault(); event.stopPropagation(); window.open(e.receiptLink, '_blank', 'noopener,noreferrer'); }}
                    style={{ color: B.goldlt, fontSize: 11, fontWeight: 700, textDecoration: 'underline', marginTop: 2, cursor: 'pointer', width: 'fit-content' }}>
                    View Receipt ↗
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', alignSelf: 'center' }}>
                <div style={{ color: B.white, fontSize: 15, fontWeight: 700 }}>{fmt$(e.amount)}</div>
              </div>
              <div style={{ alignSelf: 'center' }}>
                <div style={{ background: (e.paid_by || '').toLowerCase().includes('client') ? B.goldlt : '#334155', color: (e.paid_by || '').toLowerCase().includes('client') ? B.black : B.white, fontSize: 9, fontWeight: 800, padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase' }}>
                  {(e.paid_by || 'BUILDER').replace(/\s*paid/i, '')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DecisionsTab({ decisions, projectId, onSelect, onAdd, onReload }) {
  const [decTab, setDecTab] = useState('needed');
  const [activeCat, setActiveCat] = useState(null);
  const [search, setSearch] = useState('');

  const getPriority = (due_date) => {
    if (!due_date) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const days = Math.round((new Date(due_date + 'T12:00:00') - today) / 86400000);
    if (days < 0) return { label: 'Past Due', color: '#E74C3C', border: '#E74C3C' };
    if (days <= 2) return { label: 'Urgent', color: '#E67E22', border: '#E67E22' };
    if (days <= 7) return { label: 'High', color: '#9A732F', border: '#9A732F' };
    if (days <= 14) return { label: 'Medium', color: '#F8DA6B', border: '#F8DA6B' };
    return { label: 'Low', color: '#4CAF7D', border: '#4CAF7D' };
  };

  const getPriorityOrder = (due_date) => {
    if (!due_date) return 999;
    const today = new Date(); today.setHours(0,0,0,0);
    const days = Math.round((new Date(due_date + 'T12:00:00') - today) / 86400000);
    if (days < 0) return 0; if (days <= 2) return 1; if (days <= 7) return 2; if (days <= 14) return 3; return 4;
  };

  const catCounts = {}; const catPending = {};
  decisions.forEach(d => {
    if (d.category) {
      catCounts[d.category] = (catCounts[d.category] || 0) + 1;
      if (d.status === 'pending') catPending[d.category] = (catPending[d.category] || 0) + 1;
    }
  });

  const needed = decisions.filter(d => d.status === 'pending' && !d.is_prebuild).sort((a, b) => {
    const pa = getPriorityOrder(a.due_date), pb = getPriorityOrder(b.due_date);
    if (pa !== pb) return pa - pb;
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1; if (!b.due_date) return -1;
    return new Date(a.due_date) - new Date(b.due_date);
  });
  const prebuild = decisions.filter(d => d.is_prebuild && d.status === 'pending' && !d.parent_decision_id).sort((a, b) => (a.prebuild_sort || 0) - (b.prebuild_sort || 0));
  const awaiting = decisions.filter(d => d.status === 'pending_confirmation').sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0));
  const completed = decisions.filter(d => d.status === 'approved').sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0));
  const catNeeded = activeCat ? needed.filter(d => d.category === activeCat) : [];
  const catCompleted = activeCat ? completed.filter(d => d.category === activeCat) : [];
  const isSearching = search.trim().length > 0;
  const q = search.toLowerCase();
  const searchPending = isSearching ? needed.filter(d => d.item?.toLowerCase().includes(q) || d.notes?.toLowerCase().includes(q) || d.category?.toLowerCase().includes(q)) : [];
  const searchCompleted = isSearching ? completed.filter(d => d.item?.toLowerCase().includes(q) || d.answer?.toLowerCase().includes(q) || d.notes?.toLowerCase().includes(q) || d.category?.toLowerCase().includes(q)) : [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ color: B.white, fontSize: 18, fontWeight: 700 }}>Decisions <span style={{ color: B.muted, fontSize: 13, fontWeight: 400 }}>({decisions.length})</span></div>
        <button onClick={onAdd} style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 9, padding: '8px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 800, fontFamily: FONT }}>+ Add</button>
      </div>
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: B.muted, fontSize: 14 }}>🔍</div>
        <input placeholder="Search all decisions…" value={search} onChange={e => { setSearch(e.target.value); setActiveCat(null); }}
          style={{ width: '100%', background: B.mid, border: `1px solid ${search ? B.gold : B.slate}`, borderRadius: 10, padding: '10px 36px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box' }} />
        {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: B.muted, fontSize: 18, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>}
      </div>

      {isSearching ? (
        <div>
          {searchPending.length === 0 && searchCompleted.length === 0 && <div style={{ background: B.navy, borderRadius: 12, padding: 24, textAlign: 'center', border: `1px solid ${B.slate}`, color: B.muted, fontSize: 13 }}>No decisions match "{search}"</div>}
          {searchPending.length > 0 && <div style={{ marginBottom: 20 }}><div style={{ color: B.muted, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 10 }}>Needs Decision · {searchPending.length}</div>{searchPending.map(d => <DecisionCard key={d.id} d={d} all={decisions} onSelect={onSelect} priority={getPriority(d.due_date)} />)}</div>}
          {searchCompleted.length > 0 && <div><div style={{ color: B.muted, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 10 }}>Completed · {searchCompleted.length}</div>{searchCompleted.map(d => <DecisionCard key={d.id} d={d} all={decisions} onSelect={onSelect} completed />)}</div>}
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', background: B.slate, borderRadius: 12, padding: 3, marginBottom: 16, gap: 2 }}>
            {[{ id: 'needed', label: `Needed${needed.length > 0 ? ' (' + needed.length + ')' : ''}` }, { id: 'waiting', label: `Waiting${awaiting.length > 0 ? ' (' + awaiting.length + ')' : ''}` }, { id: 'prebuild', label: `Pre-Build${prebuild.length > 0 ? ' (' + prebuild.length + ')' : ''}` }, { id: 'completed', label: `Completed${completed.length > 0 ? ' (' + completed.length + ')' : ''}` }, { id: 'categories', label: 'Categories' }].map(t => (
              <button key={t.id} onClick={() => { setDecTab(t.id); setActiveCat(null); }}
                style={{ flex: 1, background: decTab === t.id ? B.goldlt : 'transparent', color: decTab === t.id ? B.black : B.muted, border: 'none', borderRadius: 9, padding: '8px 6px', cursor: 'pointer', fontSize: 11, fontWeight: decTab === t.id ? 800 : 400, fontFamily: FONT, whiteSpace: 'nowrap' }}>
                {t.label}
              </button>
            ))}
          </div>

          {decTab === 'needed' && (
            <div>
              {needed.length === 0 ? (
                <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}` }}><div style={{ fontSize: 32, marginBottom: 10 }}>✅</div><div style={{ color: B.muted, fontSize: 13 }}>No decisions right now.</div></div>
              ) : <div><BulkNotifyButton decisions={needed} projectId={projectId} />{needed.map(d => <DecisionCard key={d.id} d={d} all={decisions} onSelect={onSelect} priority={getPriority(d.due_date)} />)}</div>}
            </div>
          )}

          {decTab === 'prebuild' && (
            <PreBuildManager projectId={projectId} decisions={prebuild} allPrebuild={decisions.filter(d => d.is_prebuild)} onUpdate={onReload} onSelect={onSelect} />
          )}

          {decTab === 'waiting' && (
            <div>
              {awaiting.length === 0 ? (
                <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}` }}><div style={{ fontSize: 32, marginBottom: 10 }}>⏳</div><div style={{ color: B.muted, fontSize: 13 }}>Nothing waiting on client confirmation.</div></div>
              ) : awaiting.map(d => (
                <div key={d.id} onClick={() => onSelect(d)} style={{ background: B.navy, borderRadius: 12, padding: '12px 14px', marginBottom: 6, border: '1px solid #2A4258', borderLeft: '3px solid #4FA3E0', cursor: 'pointer' }}>
                  <div style={{ color: B.white, fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{d.item}</div>
                  <div style={{ color: '#4FA3E0', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.answer}</div>
                  <div style={{ color: B.muted, fontSize: 10, marginTop: 3 }}>Sent {d.completed_at ? `${new Date(d.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${new Date(d.completed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''} · awaiting client ✓</div>
                </div>
              ))}
            </div>
          )}

          {decTab === 'completed' && (
            <div>
              {completed.length === 0 ? <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}`, color: B.muted, fontSize: 13 }}>No completed decisions yet.</div>
              : completed.map(d => <DecisionCard key={d.id} d={d} all={decisions} onSelect={onSelect} completed />)}
            </div>
          )}

          {decTab === 'categories' && !activeCat && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              {CATEGORIES.map(cat => {
                const pendCount = catPending[cat] || 0;
                const totalCount = catCounts[cat] || 0;
                return (
                  <button key={cat} onClick={() => setActiveCat(cat)} style={{ background: B.navy, border: `1px solid ${pendCount > 0 ? B.gold : B.slate}`, borderRadius: 12, padding: '14px 16px', cursor: 'pointer', textAlign: 'left', fontFamily: FONT }}>
                    <div style={{ fontSize: 20, marginBottom: 6 }}>📂</div>
                    <div style={{ color: B.white, fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{cat}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <div style={{ color: B.muted, fontSize: 11 }}>{totalCount} total</div>
                      {pendCount > 0 && <div style={{ background: B.goldlt, color: B.black, borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{pendCount} needed</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {decTab === 'categories' && activeCat && (
            <div>
              <button onClick={() => setActiveCat(null)} style={{ background: 'transparent', border: 'none', color: B.goldlt, fontSize: 13, cursor: 'pointer', fontFamily: FONT, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6, padding: 0 }}>‹ Categories</button>
              <div style={{ color: B.white, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📂 {activeCat}</div>
              {catNeeded.length > 0 && <div style={{ marginBottom: 20 }}><div style={{ color: B.muted, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 10 }}>Needed · {catNeeded.length}</div>{catNeeded.map(d => <DecisionCard key={d.id} d={d} all={decisions} onSelect={onSelect} priority={getPriority(d.due_date)} />)}</div>}
              {catCompleted.length > 0 && <div><div style={{ color: B.muted, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 10 }}>Completed · {catCompleted.length}</div>{catCompleted.map(d => <DecisionCard key={d.id} d={d} all={decisions} onSelect={onSelect} completed />)}</div>}
              {catNeeded.length === 0 && catCompleted.length === 0 && <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}`, color: B.muted, fontSize: 13 }}>No decisions in this category yet.</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DecisionCard({ d, onSelect, completed, priority, all = [] }) {
  const borderColor = completed ? '#4CAF7D' : priority?.border || B.slate;
  const parent = d.parent_decision_id ? all.find(x => x.id === d.parent_decision_id) : null;
  const thumbUrl = d.photo_drive_id ? `https://drive.google.com/thumbnail?id=${d.photo_drive_id}&sz=w120` : null;
  return (
    <button onClick={() => onSelect(d)} style={{ width: '100%', background: B.navy, borderRadius: 12, padding: '14px 16px', marginBottom: 8, border: `1px solid ${B.slate}`, borderLeft: `3px solid ${borderColor}`, cursor: 'pointer', textAlign: 'left', fontFamily: FONT }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {parent && (
            <div style={{ color: '#4FA3E0', fontSize: 11, fontWeight: 700, marginBottom: 2 }}>
              {parent.item}{d.parent_option ? ` · ${d.parent_option}` : ''}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
            <div style={{ color: B.white, fontSize: 14, fontWeight: 700 }}>{parent ? '↳ ' : ''}{d.item}</div>
            {!completed && priority && <div style={{ background: `${priority.color}22`, color: priority.color, border: `1px solid ${priority.color}44`, borderRadius: 6, padding: '1px 7px', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{priority.label}</div>}
          </div>
          {d.category && <div style={{ color: B.gold, fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{d.category}</div>}
          {completed && d.answer && <div style={{ color: '#4CAF7D', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: thumbUrl ? 'normal' : 'nowrap' }}>{d.answer}</div>}
          {!completed && d.notes && <div style={{ color: B.muted, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.notes}</div>}
          {!completed && d.due_date && <div style={{ color: priority?.color || B.muted, fontSize: 11, marginTop: 3 }}>Due {fmtDate(d.due_date)}</div>}
          {completed && d.completed_by && <div style={{ color: B.muted, fontSize: 10, marginTop: 4, textTransform: 'capitalize' }}>{d.completed_by} · {d.completed_at ? fmtDate(d.completed_at?.split('T')[0]) : ''}</div>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
          {thumbUrl && <img src={thumbUrl} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: `1px solid ${B.slate}` }} onError={e => e.target.style.display='none'} />}
          <div style={{ color: B.muted, fontSize: 18 }}>›</div>
        </div>
      </div>
    </button>
  );
}

function BulkNotifyButton({ decisions, projectId }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const handleBulkNotify = async () => {
    setSending(true);
    const { notifyProjectClients } = await import('../hooks/usePushNotifications');
    await notifyProjectClients(projectId, `${decisions.length} Decision${decisions.length !== 1 ? 's' : ''} Need Your Input`, decisions.slice(0, 3).map(d => d.item).join(', '), `${window.location.origin}?project=${projectId}&tab=decisions`);
    setSending(false); setSent(true); setTimeout(() => setSent(false), 3000);
  };
  return (
    <button onClick={handleBulkNotify} disabled={sending}
      style={{ width: '100%', background: sent ? 'rgba(76,175,61,0.15)' : 'rgba(248,218,107,0.08)', border: `1px solid ${sent ? '#4CAF7D' : B.gold}`, borderRadius: 10, padding: '10px', cursor: sending ? 'default' : 'pointer', color: sent ? '#4CAF7D' : B.goldlt, fontSize: 13, fontWeight: 700, fontFamily: FONT, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
      {sending ? 'Sending…' : sent ? '✓ Clients Notified!' : `🔔 Notify Client — ${decisions.length} Decision${decisions.length !== 1 ? 's' : ''} Pending`}
    </button>
  );
}

const LOG_SHEET_IDS = { 1: '10XOOYS3yaXsmwf4wk5BHOXO1Y6LJ5c5qKMPzfAZShJ0', 2: '1TeirSGjgSZalSDpyxxAHFImBZmmzCD7xeU99Hb4rAck', 3: '12aZNIwtf4z8jveRGFyWRfHlqwvIe4lCGrPfj0RUHCcs', 4: '1Q-GKQn4KMssGCRIg9o2AnFUT6CcyKQ5GVFd4-lWP-FE' };

function LogTab({ logs, projectId, onNewEntry, onRefresh }) {
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const syncLogData = async () => {
    const sheetId = LOG_SHEET_IDS[parseInt(projectId)];
    if (!sheetId) return;
    setSyncing(true);
    try {
      const token = await ensureToken();
      if (!token) { setSyncing(false); return; }
      const { data: dbLogs } = await supabase.from('daily_logs').select('*').eq('project_id', projectId);
      const appMap = new Map((dbLogs || []).map(l => [l.log_date, l]));
      const res = await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/Daily%20Log!A:I?majorDimension=ROWS`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.error) { setSyncing(false); return; }
      const sheetRows = (data.values || []).slice(1).filter(r => r[0] && r[0].trim());
      const normalizeDate = (raw) => {
        const s = raw.trim();
        if (s.includes('/')) {
          const parts = s.split('/');
          if (parts.length === 3) { let year = parts[2]; if (year.length === 2) year = '20' + year; return `${year}-${parts[0].padStart(2,'0')}-${parts[1].padStart(2,'0')}`; }
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
        return null;
      };
      const toUpsert = [];
      for (const r of sheetRows) {
        const log_date = normalizeDate(r[0] || '');
        if (!log_date) continue;
        if (appMap.has(log_date)) continue;
        const trades = r[3] ? r[3].split(',').map(s => s.trim()).filter(Boolean) : [];
        const notes = r[5]?.trim() || null;
        const weather = r[2]?.trim() || null;
        if (!notes && trades.length === 0 && !weather) continue;
        toUpsert.push({ project_id: parseInt(projectId), log_date, weather, trades, notes, deliveries: r[6] ? r[6].split(',').map(s => s.trim()).filter(Boolean) : [], inspection: r[7]?.trim() || null });
      }
      for (let i = 0; i < toUpsert.length; i += 50) {
        await supabase.from('daily_logs').upsert(toUpsert.slice(i, i + 50), { onConflict: 'project_id,log_date' });
      }
      setLastSync(new Date().toLocaleTimeString());
      onRefresh();
    } catch(e) { console.error('Log sync error:', e); }
    setSyncing(false);
  };

  useEffect(() => { if (localStorage.getItem('google_access_token')) syncLogData(); }, [projectId]);

  const exportCSV = () => {
    const headers = ['Date', 'Weather', 'Trades', 'Deliveries', 'Notes'];
    const rows = logs.map(e => [e.log_date, e.weather || '', (e.trades || []).join('; '), (e.deliveries || []).join('; '), (e.notes || '').replace(/,/g, ';')]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `daily-log-project-${projectId}-${(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const win = window.open('', '_blank');
    const rows = logs.map(e => `<tr><td style="padding:8px;border-bottom:1px solid #eee;font-weight:600">${e.log_date}</td><td style="padding:8px;border-bottom:1px solid #eee">${e.weather || '—'}</td><td style="padding:8px;border-bottom:1px solid #eee">${(e.trades || []).join(', ') || '—'}</td><td style="padding:8px;border-bottom:1px solid #eee;max-width:300px">${e.notes || '—'}</td></tr>`).join('');
    win.document.write(`<html><head><title>Daily Log</title><style>body{font-family:sans-serif;padding:32px;color:#111}h1{color:#9A732F}table{width:100%;border-collapse:collapse}th{background:#9A732F;color:#fff;padding:10px 8px;text-align:left}tr:nth-child(even){background:#f9f9f9}@media print{button{display:none}}</style></head><body><h1>Muller Homes — Daily Log</h1><p style="color:#666">Project ID: ${projectId} | Exported: ${new Date().toLocaleDateString()}</p><table><thead><tr><th>Date</th><th>Weather</th><th>Trades</th><th>Notes</th></tr></thead><tbody>${rows}</tbody></table><br/><button onclick="window.print()" style="background:#9A732F;color:#fff;border:none;padding:10px 24px;border-radius:6px;cursor:pointer;font-size:14px">Print / Save as PDF</button></body></html>`);
    win.document.close();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ color: B.white, fontSize: 18, fontWeight: 700 }}>Daily Log <span style={{ color: B.muted, fontSize: 13, fontWeight: 400 }}>({logs.length})</span></div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {lastSync && <div style={{ color: B.muted, fontSize: 10 }}>Synced {lastSync}</div>}
          {syncing && <div style={{ color: B.muted, fontSize: 11 }}>Syncing…</div>}
          <button onClick={exportCSV} style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 8, padding: '7px 10px', cursor: 'pointer', color: B.muted, fontSize: 11, fontFamily: FONT }}>CSV</button>
          <button onClick={exportPDF} style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 8, padding: '7px 10px', cursor: 'pointer', color: B.muted, fontSize: 11, fontFamily: FONT }}>PDF</button>
          <button onClick={syncLogData} style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: B.muted, fontSize: 12, fontFamily: FONT }}>↻</button>
          <button onClick={onNewEntry} style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 9, padding: '9px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: FONT }}>+ New Entry</button>
        </div>
      </div>
      {logs.length === 0 ? (
        <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}` }}><div style={{ color: B.muted, fontSize: 13 }}>No log entries yet.</div></div>
      ) : logs.map(e => <LogEntryCard key={e.id} entry={e} onRefresh={onRefresh} />)}
    </div>
  );
}

function LogEntryCard({ entry: e, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(e.notes || '');
  const [weather, setWeather] = useState(e.weather || '');
  const [trades, setTrades] = useState((e.trades || []).join(', '));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await supabase.from('daily_logs').update({ notes: notes.trim() || null, weather: weather.trim() || null, trades: trades.split(',').map(s => s.trim()).filter(Boolean) }).eq('id', e.id);
    setSaving(false); setEditing(false); onRefresh();
  };

  if (editing) return (
    <div style={{ background: B.navy, borderRadius: 12, padding: 18, marginBottom: 12, border: `1px solid ${B.gold}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ color: B.white, fontSize: 14, fontWeight: 700 }}>{fmtDate(e.log_date)}</div>
        <button onClick={() => setEditing(false)} style={{ background: 'none', border: 'none', color: B.muted, fontSize: 18, cursor: 'pointer' }}>×</button>
      </div>
      <input value={weather} onChange={ev => setWeather(ev.target.value)} placeholder="Weather" style={{ width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 8, padding: '8px 10px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', marginBottom: 8 }} />
      <input value={trades} onChange={ev => setTrades(ev.target.value)} placeholder="Trades (comma separated)" style={{ width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 8, padding: '8px 10px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', marginBottom: 8 }} />
      <textarea value={notes} onChange={ev => setNotes(ev.target.value)} placeholder="Notes" rows={4} style={{ width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 8, padding: '8px 10px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5, marginBottom: 10 }} />
      <button onClick={handleSave} disabled={saving} style={{ width: '100%', background: B.goldlt, color: B.black, border: 'none', borderRadius: 9, padding: 12, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: FONT }}>{saving ? 'Saving…' : 'Save Changes'}</button>
    </div>
  );

  return (
    <div style={{ background: B.navy, borderRadius: 12, padding: 18, marginBottom: 12, border: `1px solid ${B.slate}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ color: B.white, fontSize: 14, fontWeight: 700 }}>{fmtDate(e.log_date)}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {e.weather && <div style={{ color: B.goldlt, fontSize: 12 }}>{e.weather}</div>}
          <button onClick={() => setEditing(true)} style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 6, padding: '3px 8px', cursor: 'pointer', color: B.muted, fontSize: 11, fontFamily: FONT }}>Edit</button>
        </div>
      </div>
      {e.trades?.length > 0 && <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>{e.trades.map(t => <span key={t} style={{ background: B.slate, color: B.white, borderRadius: 4, padding: '2px 8px', fontSize: 11 }}>{t}</span>)}</div>}
      {e.deliveries?.length > 0 && <div style={{ marginBottom: 6 }}><span style={{ color: B.muted, fontSize: 11 }}>Deliveries: </span><span style={{ color: B.white, fontSize: 12 }}>{e.deliveries.join(', ')}</span></div>}
      {e.inspection && <div style={{ color: '#4CAF7D', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{e.inspection}</div>}
      {e.notes && <div style={{ color: B.light, fontSize: 13, lineHeight: 1.6, borderTop: `1px solid ${B.slate}`, paddingTop: 8, marginTop: 6 }}>{e.notes}</div>}
    </div>
  );
}

function SortableTasksList({ tasks, onComplete, onUpdate, onReorder }) {
  const [order, setOrder] = useState(tasks);
  const [draggingId, setDraggingId] = useState(null);
  const rowRefs = useRef({});

  // Re-sync when tasks change from parent (e.g. after add/complete/refetch)
  useEffect(() => { setOrder(tasks); }, [tasks.map(t => t.id).join(','), tasks.length]);

  // Prevent page scroll while dragging
  useEffect(() => {
    if (!draggingId) return;
    const preventScroll = (e) => e.preventDefault();
    document.addEventListener('touchmove', preventScroll, { passive: false });
    return () => document.removeEventListener('touchmove', preventScroll);
  }, [draggingId]);

  const handleDragStart = (id) => (e) => {
    e.stopPropagation();
    setDraggingId(id);
    if (navigator.vibrate) navigator.vibrate(15);
  };

  const handleMove = (e) => {
    if (!draggingId) return;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const currentIdx = order.findIndex(t => t.id === draggingId);
    if (currentIdx < 0) return;

    let targetIdx = currentIdx;
    for (let i = 0; i < order.length; i++) {
      if (i === currentIdx) continue;
      const el = rowRefs.current[order[i].id];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      if (i < currentIdx && y < midY) { targetIdx = i; break; }
      if (i > currentIdx && y > midY) targetIdx = i;
    }

    if (targetIdx !== currentIdx) {
      const newOrder = [...order];
      const [moved] = newOrder.splice(currentIdx, 1);
      newOrder.splice(targetIdx, 0, moved);
      setOrder(newOrder);
    }
  };

  const handleEnd = async () => {
    if (draggingId) {
      const changed = order.some((t, i) => t.id !== tasks[i]?.id);
      if (changed) await onReorder(order);
    }
    setDraggingId(null);
  };

  return (
    <div
      onTouchMove={handleMove}
      onTouchEnd={handleEnd}
      onTouchCancel={handleEnd}
      onMouseMove={draggingId ? handleMove : undefined}
      onMouseUp={draggingId ? handleEnd : undefined}
      onMouseLeave={draggingId ? handleEnd : undefined}
    >
      {order.map(task => (
        <div
          key={task.id}
          ref={el => rowRefs.current[task.id] = el}
          style={{
            opacity: draggingId === task.id ? 0.45 : 1,
            transition: draggingId ? 'none' : 'opacity 0.15s',
          }}
        >
          <ProjectTaskRow
            task={task}
            onComplete={() => onComplete(task.id)}
            onUpdate={onUpdate}
            onDragHandleTouch={handleDragStart(task.id)}
          />
        </div>
      ))}
    </div>
  );
}

function ProjectTaskRow({ task: t, onComplete, onUpdate, onDragHandleTouch }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(t.text);
  const [priority, setPriority] = useState(t.priority || 'medium');
  const [saving, setSaving] = useState(false);
  const [description, setDescription] = useState(t.description || '');
  const [link, setLink] = useState(t.link || '');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [fullPhoto, setFullPhoto] = useState(null);
  useBackToClose(editing, () => setEditing(false));
  useBackToClose(!!fullPhoto, () => setFullPhoto(null));

  const handleSave = async () => {
    setSaving(true);
    let photo_url = t.photo_url || null;
    if (photoFile) {
      try {
        const path = `tasks/${t.id}_${Date.now()}_${photoFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
        const { error: upErr } = await supabase.storage.from('purchase-photos').upload(path, photoFile);
        if (!upErr) {
          const { data } = supabase.storage.from('purchase-photos').getPublicUrl(path);
          photo_url = data.publicUrl;
        }
      } catch(e) { console.error('Task photo upload failed:', e); }
    }
    await supabase.from('todos').update({ text: text.trim(), priority, description: description.trim() || null, link: link.trim() || null, photo_url }).eq('id', t.id);
    setSaving(false); setEditing(false); onUpdate();
  };

  const handleDelete = async () => {
    await supabase.from('todos').delete().eq('id', t.id);
    onUpdate();
  };

  if (editing) return (
    <div style={{ background: B.mid, borderRadius: 11, padding: 14, marginBottom: 8, border: `1px solid ${B.gold}` }}>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={2} autoFocus style={{ width: '100%', background: B.navy, border: 'none', borderRadius: 8, padding: '10px 12px', color: B.white, fontSize: 14, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', resize: 'none', marginBottom: 10 }} />
      <input value={link} onChange={e => setLink(e.target.value)} placeholder="🔗 Link (optional)"
        style={{ width: '100%', background: B.navy, border: 'none', borderRadius: 8, padding: '9px 12px', color: B.white, fontSize: 12, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', marginBottom: 8 }} />
      <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="📝 Description / details (optional)"
        style={{ width: '100%', background: B.navy, border: 'none', borderRadius: 8, padding: '9px 12px', color: B.white, fontSize: 12, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', resize: 'vertical', marginBottom: 8 }} />
      {(photoPreview || t.photo_url) && (
        <img src={photoPreview || t.photo_url} alt=""
          onClick={() => setFullPhoto(photoPreview || t.photo_url)}
          style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 9, marginBottom: 8, border: `1px solid ${B.slate}`, cursor: 'zoom-in' }} />
      )}
      <label style={{ display: 'block', border: `1px dashed ${B.slate}`, borderRadius: 9, padding: '8px 12px', cursor: 'pointer', color: B.muted, fontSize: 11, textAlign: 'center', marginBottom: 10 }}>
        📷 {t.photo_url || photoPreview ? 'Replace photo' : 'Add photo (optional)'}
        <input type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) { setPhotoFile(file); setPhotoPreview(URL.createObjectURL(file)); }
          }} />
      </label>
      {fullPhoto && (
        <div onClick={() => setFullPhoto(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}>
          <img src={fullPhoto} alt="" style={{ maxWidth: '100vw', maxHeight: '100vh', objectFit: 'contain' }} />
          <button onClick={() => setFullPhoto(null)}
            style={{ position: 'fixed', top: 18, right: 18, background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {['low', 'medium', 'high'].map(p => (
          <button key={p} onClick={() => setPriority(p)} style={{ flex: 1, background: priority === p ? B.goldlt : B.slate, color: priority === p ? B.black : B.muted, border: 'none', borderRadius: 7, padding: '6px', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: FONT, textTransform: 'capitalize' }}>{p}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleSave} disabled={saving} style={{ flex: 1, background: B.goldlt, color: B.black, border: 'none', borderRadius: 9, padding: '10px', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: FONT }}>{saving ? '…' : 'Save'}</button>
        <button onClick={() => setEditing(false)} style={{ flex: 1, background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 9, padding: '10px', cursor: 'pointer', color: B.muted, fontSize: 13, fontFamily: FONT }}>Cancel</button>
        <button onClick={handleDelete} style={{ background: 'transparent', border: '1px solid #E74C3C', borderRadius: 9, padding: '10px 14px', cursor: 'pointer', color: '#E74C3C', fontSize: 13, fontFamily: FONT }}>Delete</button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, background: B.navy, borderRadius: 11, padding: '14px 16px', marginBottom: 8, border: `1px solid ${B.slate}`, borderLeft: `3px solid ${t.priority === 'high' ? B.goldlt : B.slate}` }}>
      <button onClick={e => { e.stopPropagation(); onComplete(); }} style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${B.gold}`, background: 'transparent', cursor: 'pointer', flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, cursor: 'pointer', minWidth: 0 }} onClick={() => setEditing(true)}>
        <div style={{ color: B.white, fontSize: 14, lineHeight: 1.5 }}>{t.text}
          {(t.description || t.link || t.photo_url) && (
            <span style={{ color: '#6F6F6F', fontSize: 10, marginLeft: 6 }}>
              {t.photo_url ? '📷' : ''}{t.link ? '🔗' : ''}{t.description ? '📝' : ''}
            </span>
          )}
        </div>
        <div style={{ color: t.priority === 'high' ? B.goldlt : '#6F6F6F', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{t.priority}</div>
      </div>
      <button
        onClick={async (e) => {
          e.stopPropagation();
          if (t.focus) {
            await supabase.from('todos').update({ focus: false }).eq('id', t.id);
          } else {
            const { data: maxRow } = await supabase.from('todos').select('focus_sort').eq('focus', true).order('focus_sort', { ascending: false }).limit(1);
            const nextSort = ((maxRow && maxRow[0]?.focus_sort) || 0) + 1;
            await supabase.from('todos').update({ focus: true, focus_sort: nextSort }).eq('id', t.id);
          }
          onUpdate();
          window.dispatchEvent(new Event('focuslist-changed'));
        }}
        title={t.focus ? 'On your To-Do list — tap to remove' : 'Add to your To-Do list'}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 15, padding: '0 2px', flexShrink: 0, alignSelf: 'center', opacity: t.focus ? 1 : 0.35 }}>
        📌
      </button>
      {onDragHandleTouch && (
        <div
          onTouchStart={onDragHandleTouch}
          onMouseDown={onDragHandleTouch}
          style={{ padding: '4px 6px', color: '#6F6F6F', fontSize: 20, userSelect: 'none', touchAction: 'none', cursor: 'grab', lineHeight: 1, flexShrink: 0, alignSelf: 'center' }}>
          ≡
        </div>
      )}
    </div>
  );
}


function BuilderNotesTab({ project }) {
  const [notes, setNotes] = useState([]);
  const [view, setView] = useState('active');
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await supabase.from('client_notes').select('*')
      .eq('project_id', project.id).order('created_at', { ascending: false });
    if (data) setNotes(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [project.id]);

  const sendReply = async (note, text, isEdit) => {
    if (!text.trim()) return;
    setSaving(true);
    await supabase.from('client_notes').update({
      reply: text.trim(),
      status: 'answered',
      replied_at: new Date().toISOString(),
    }).eq('id', note.id);
    // Responding clears the matching notification from the inbox
    try {
      await supabase.from('app_notifications').update({ read: true })
        .eq('recipient_role', 'builder')
        .eq('project_id', parseInt(project.id))
        .eq('read', false)
        .eq('body', (note.content || '').trim().slice(0, 100));
    } catch(e) {}
    try {
      const { notifyProjectClients } = await import('../hooks/usePushNotifications');
      await notifyProjectClients(
        project.id,
        isEdit ? `💬 Dan updated his response` : `💬 Dan responded`,
        text.trim().slice(0, 100),
        `${window.location.origin}?project=${project.id}&tab=notes`
      );
    } catch(e) {}
    setReplyingTo(null);
    setReplyText('');
    setSaving(false);
    load();
  };

  const quickReply = (note, text) => sendReply(note, text, !!note.reply);

  const [taskedNoteIds, setTaskedNoteIds] = useState([]);
  const addNoteAsTask = async (n) => {
    await supabase.from('todos').insert({
      project_id: parseInt(project.id),
      text: n.content.length > 120 ? n.content.slice(0, 117) + '…' : n.content,
      priority: 'medium',
    });
    setTaskedNoteIds(prev => [...prev, n.id]);
  };

  const openQuestions = notes.filter(n => n.type === 'question' && n.status === 'open');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ color: B.white, fontSize: 18, fontWeight: 700 }}>
          Client Notes
          {openQuestions.length > 0 && (
            <span style={{ background: B.goldlt, color: B.black, borderRadius: 10, padding: '2px 9px', fontSize: 11, fontWeight: 700, marginLeft: 10, verticalAlign: 'middle' }}>
              {openQuestions.length} to answer
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ color: B.muted, textAlign: 'center', padding: 32 }}>Loading…</div>
      ) : notes.length === 0 ? (
        <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}` }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📝</div>
          <div style={{ color: B.muted, fontSize: 13 }}>No client notes or questions yet.</div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {[
              { v: 'active', label: `Active (${notes.filter(n => !n.reply).length})` },
              { v: 'completed', label: `✓ Completed (${notes.filter(n => n.reply).length})` },
            ].map(opt => (
              <button key={opt.v} onClick={() => setView(opt.v)}
                style={{ flex: 1, background: view === opt.v ? 'rgba(248,218,107,0.1)' : 'transparent', border: `1px solid ${view === opt.v ? B.gold : B.slate}`, borderRadius: 9, padding: '9px', cursor: 'pointer', color: view === opt.v ? B.goldlt : B.muted, fontSize: 12, fontWeight: 700, fontFamily: FONT }}>
                {opt.label}
              </button>
            ))}
          </div>
          {(view === 'active' ? notes.filter(n => !n.reply) : notes.filter(n => n.reply)).length === 0 && (
            <div style={{ color: B.muted, fontSize: 12, textAlign: 'center', padding: 24 }}>
              {view === 'active' ? 'Nothing waiting — all caught up.' : 'Nothing completed yet.'}
            </div>
          )}
          {[
            { items: view === 'active' ? notes.filter(n => !n.reply) : notes.filter(n => n.reply), completedSection: view === 'completed' },
          ].map(({ items, completedSection }) => (
            <div key={completedSection ? 'done' : 'active'}>
              {items.map(n => {
        const isOpen = n.type === 'question' && n.status === 'open';
        const isReplying = replyingTo === n.id;
        return (
          <div key={n.id} style={{ background: B.navy, borderRadius: 12, padding: 16, marginBottom: 10, border: `1px solid ${isOpen ? B.gold : B.slate}`, borderLeft: `3px solid ${n.reply ? '#4CAF7D' : (n.type === 'question' ? B.goldlt : B.slate)}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ color: n.type === 'question' ? B.goldlt : B.muted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
                {n.type === 'question' ? (n.reply ? '✓ Question — Answered' : '❓ Question — Needs Answer') : (n.reply ? '✓ Note — Acknowledged' : '📝 Note')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {taskedNoteIds.includes(n.id) ? (
                  <span style={{ color: '#4CAF7D', fontSize: 10, fontWeight: 700 }}>✓ Tasked</span>
                ) : (
                  <button onClick={() => addNoteAsTask(n)}
                    style={{ background: 'rgba(248,218,107,0.08)', border: `1px solid ${B.gold}`, borderRadius: 7, padding: '3px 9px', cursor: 'pointer', color: B.goldlt, fontSize: 10, fontWeight: 700, fontFamily: FONT }}>
                    → Task
                  </button>
                )}
                <div style={{ color: B.muted, fontSize: 11 }}>{fmtDate(n.created_at?.split('T')[0])}</div>
              </div>
            </div>
            <div style={{ color: B.white, fontSize: 14, lineHeight: 1.5 }}>{n.content}</div>

            {n.reply && !isReplying && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${B.slate}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div style={{ color: '#4CAF7D', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Your Response</div>
                  <button onClick={() => { setReplyingTo(n.id); setReplyText(n.reply); }}
                    style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 6, padding: '3px 10px', cursor: 'pointer', color: B.muted, fontSize: 11, fontFamily: FONT }}>
                    Edit
                  </button>
                </div>
                <div style={{ color: B.light, fontSize: 14, lineHeight: 1.5 }}>{n.reply}</div>
              </div>
            )}

            {isReplying && (
              <div style={{ marginTop: 12 }}>
                <textarea value={replyText} onChange={e => setReplyText(e.target.value)} autoFocus rows={3}
                  placeholder="Type your response…"
                  style={{ width: '100%', background: B.mid, border: `1px solid ${B.gold}`, borderRadius: 9, padding: '10px 12px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5, marginBottom: 8 }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => sendReply(n, replyText, !!n.reply)} disabled={saving || !replyText.trim()}
                    style={{ flex: 1, background: replyText.trim() ? B.goldlt : B.slate, color: replyText.trim() ? B.black : B.muted, border: 'none', borderRadius: 9, padding: 11, cursor: 'pointer', fontSize: 13, fontWeight: 800, fontFamily: FONT }}>
                    {saving ? 'Sending…' : n.reply ? 'Update Response' : 'Send Response'}
                  </button>
                  <button onClick={() => { setReplyingTo(null); setReplyText(''); }}
                    style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 9, padding: '11px 16px', cursor: 'pointer', color: B.muted, fontSize: 13, fontFamily: FONT }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!n.reply && !isReplying && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                <button onClick={() => quickReply(n, n.type === 'question' ? 'Yes' : 'Got it 👍')}
                  style={{ background: 'rgba(76,175,61,0.12)', border: '1px solid #4CAF7D', borderRadius: 9, padding: '8px 16px', cursor: 'pointer', color: '#4CAF7D', fontSize: 12, fontWeight: 700, fontFamily: FONT }}>
                  {n.type === 'question' ? '👍 Yes' : '✓ Got it'}
                </button>
                {n.type === 'question' && (
                  <button onClick={() => quickReply(n, 'No')}
                    style={{ background: 'rgba(231,76,60,0.1)', border: '1px solid #E74C3C', borderRadius: 9, padding: '8px 16px', cursor: 'pointer', color: '#E74C3C', fontSize: 12, fontWeight: 700, fontFamily: FONT }}>
                    👎 No
                  </button>
                )}
                <button onClick={() => { setReplyingTo(n.id); setReplyText(''); }}
                  style={{ background: 'rgba(248,218,107,0.08)', border: `1px solid ${B.gold}`, borderRadius: 9, padding: '8px 16px', cursor: 'pointer', color: B.goldlt, fontSize: 12, fontWeight: 700, fontFamily: FONT }}>
                  💬 Comment
                </button>
              </div>
            )}
          </div>
        );
      })}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function OverviewNotesCard({ projectId, onOpen }) {
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    supabase.from('client_notes').select('*').eq('project_id', projectId)
      .order('created_at', { ascending: false }).limit(10)
      .then(({ data }) => { if (data) setNotes(data); });
  }, [projectId]);

  const openQs = notes.filter(n => n.type === 'question' && n.status === 'open');

  return (
    <div style={{ background: B.navy, borderRadius: 14, padding: 18, marginBottom: 12, border: `1px solid ${openQs.length > 0 ? B.gold : B.slate}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: notes.length > 0 ? 8 : 0 }}>
        <div onClick={onOpen} style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', cursor: 'pointer' }}>Notes ›</div>
        <div style={{ color: openQs.length > 0 ? B.goldlt : B.muted, fontSize: 11, fontWeight: openQs.length > 0 ? 700 : 400 }}>
          {openQs.length > 0 ? `${openQs.length} question${openQs.length !== 1 ? 's' : ''} to answer` : `${notes.length} recent`}
        </div>
      </div>
      {notes.length === 0 ? (
        <div style={{ color: B.muted, fontSize: 12, textAlign: 'center', padding: '4px 0' }}>No client notes yet.</div>
      ) : notes.slice(0, 3).map(n => (
        <div key={n.id} onClick={onOpen} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 6, marginBottom: 6, borderBottom: `1px solid ${B.slate}`, cursor: 'pointer' }}>
          <div style={{ color: B.white, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {n.type === 'question' ? '❓ ' : '📝 '}{n.content}
          </div>
          {n.type === 'question' && n.status === 'open' && <div style={{ color: B.goldlt, fontSize: 10, fontWeight: 700, marginLeft: 8, flexShrink: 0 }}>OPEN</div>}
        </div>
      ))}
    </div>
  );
}

function OverviewPurchasesCard({ projectId, onOpen }) {
  const [purchases, setPurchases] = useState([]);
  useEffect(() => {
    supabase.from('purchases').select('*').eq('project_id', projectId)
      .then(({ data }) => { if (data) setPurchases(data); });
  }, [projectId]);

  const needed = purchases.filter(p => p.status === 'needed');

  return (
    <div style={{ background: B.navy, borderRadius: 14, padding: 18, marginBottom: 12, border: `1px solid ${needed.length > 0 ? B.gold : B.slate}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: needed.length > 0 ? 8 : 0 }}>
        <div onClick={onOpen} style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', cursor: 'pointer' }}>Purchases ›</div>
        <div style={{ color: B.muted, fontSize: 11 }}>{needed.length} needed · {purchases.length} total</div>
      </div>
      {needed.length === 0 ? (
        <div style={{ color: B.muted, fontSize: 12, textAlign: 'center', padding: '4px 0' }}>Nothing currently needed.</div>
      ) : needed.slice(0, 3).map(p => (
        <div key={p.id} onClick={onOpen} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 6, marginBottom: 6, borderBottom: `1px solid ${B.slate}`, cursor: 'pointer' }}>
          <div style={{ color: B.white, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{p.item}</div>
          {p.needed_by && <div style={{ color: B.goldlt, fontSize: 11, marginLeft: 8, flexShrink: 0 }}>{fmtDate(p.needed_by)}</div>}
        </div>
      ))}
      {needed.length > 3 && <div style={{ color: B.muted, fontSize: 11 }}>+{needed.length - 3} more</div>}
    </div>
  );
}

function NotificationsTab({ projectId, role }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await supabase.from('app_notifications').select('*')
      .eq('project_id', parseInt(projectId)).eq('recipient_role', role)
      .order('created_at', { ascending: false }).limit(100);
    if (data) setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [projectId]);

  const markRead = async (id) => {
    await supabase.from('app_notifications').update({ read: true }).eq('id', id);
    setItems(prev => prev.map(i => i.id === id ? { ...i, read: true } : i));
  };

  const markAllRead = async () => {
    await supabase.from('app_notifications').update({ read: true })
      .eq('project_id', parseInt(projectId)).eq('recipient_role', role).eq('read', false);
    setItems(prev => prev.map(i => ({ ...i, read: true })));
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ color: B.white, fontSize: 18, fontWeight: 700 }}>Notifications{items.filter(i => !i.read).length > 0 ? ` (${items.filter(i => !i.read).length})` : ''}</div>
        {items.length > 1 && (
          <button onClick={markAllRead}
            style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: B.muted, fontSize: 11, fontWeight: 700, fontFamily: FONT }}>
            ✓ Mark all read
          </button>
        )}
      </div>
      {loading ? (
        <div style={{ color: B.muted, textAlign: 'center', padding: 32 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}` }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>🔔</div>
          <div style={{ color: B.muted, fontSize: 13 }}>You're all caught up.</div>
        </div>
      ) : [...items.filter(i => !i.read), ...items.filter(i => i.read)].map(n => (
        <div key={n.id} style={{ background: B.navy, borderRadius: 12, padding: '13px 15px', marginBottom: 8, border: `1px solid ${B.slate}`, borderLeft: `3px solid ${n.read ? '#2A2A2A' : B.goldlt}`, opacity: n.read ? 0.55 : 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0, cursor: n.url ? 'pointer' : 'default' }}
              onClick={() => { if (n.url) { markRead(n.id); setTimeout(() => { window.location.href = n.url; }, 120); } }}>
              <div style={{ color: B.white, fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{n.title}</div>
              {n.body && <div style={{ color: B.light, fontSize: 12, lineHeight: 1.5 }}>{n.body}</div>}
              <div style={{ color: '#555', fontSize: 10, marginTop: 4 }}>
                {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {new Date(n.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </div>
            </div>
            {!n.read && (
              <button onClick={() => markRead(n.id)}
                style={{ background: 'rgba(76,175,61,0.1)', border: '1px solid #4CAF7D', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#4CAF7D', fontSize: 11, fontWeight: 700, fontFamily: FONT, flexShrink: 0 }}>
                ✓ Read
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}


function PreBuildManager({ projectId, decisions, allPrebuild = [], onUpdate, onSelect }) {
  const [view, setView] = useState('build'); // 'build' | 'master'
  const [master, setMaster] = useState([]);
  const [item, setItem] = useState('');
  const [category, setCategory] = useState('');
  const [answerType, setAnswerType] = useState('options');
  const [optList, setOptList] = useState(['', '']);
  const [costList, setCostList] = useState(['', '']);
  const [comments, setComments] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingMaster, setLoadingMaster] = useState(false);
  const [locked, setLocked] = useState(null);
  const [followUps, setFollowUps] = useState({});
  const [dragId, setDragId] = useState(null);
  const [order, setOrder] = useState([]);
  const rowRefs = useRef({});

  const setFU = (i, patch) => setFollowUps(prev => ({ ...prev, [i]: { ...(prev[i] || { name: '', type: 'text', opts: ['', ''] }), ...patch } }));
  const removeFU = (i) => setFollowUps(prev => { const n = { ...prev }; delete n[i]; return n; });

  const loadMaster = async () => {
    const { data } = await supabase.from('prebuild_master').select('*').order('sort');
    if (data) setMaster(data);
  };
  useEffect(() => { loadMaster(); }, []);
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('projects').select('prebuild_locked').eq('id', parseInt(projectId)).single();
      setLocked(!!data?.prebuild_locked);
    })();
  }, [projectId]);

  const toggleLock = async () => {
    const next = !locked;
    setLocked(next);
    await supabase.from('projects').update({ prebuild_locked: next }).eq('id', parseInt(projectId));
  };

  const topList = view === 'master' ? master.filter(m => !m.parent_master_id) : decisions;
  useEffect(() => { setOrder(topList); }, [view, master.map(m => m.id).join(','), decisions.map(d => d.id).join(',')]);

  // ---- drag (same pattern as tasks) ----
  useEffect(() => {
    if (!dragId) return;
    const prevent = (e) => e.preventDefault();
    document.addEventListener('touchmove', prevent, { passive: false });
    return () => document.removeEventListener('touchmove', prevent);
  }, [dragId]);

  const dragStart = (id) => (e) => {
    e.stopPropagation();
    setDragId(id);
    if (navigator.vibrate) navigator.vibrate(15);
  };

  const dragMove = (e) => {
    if (!dragId) return;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const cur = order.findIndex(t => t.id === dragId);
    if (cur < 0) return;
    let target = cur;
    for (let i = 0; i < order.length; i++) {
      if (i === cur) continue;
      const el = rowRefs.current[order[i].id];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      if (i < cur && y < midY) { target = i; break; }
      if (i > cur && y > midY) target = i;
    }
    if (target !== cur) {
      const n = [...order];
      const [moved] = n.splice(cur, 1);
      n.splice(target, 0, moved);
      setOrder(n);
    }
  };

  const dragEnd = async () => {
    if (dragId) {
      const changed = order.some((t, i) => t.id !== topList[i]?.id);
      if (changed) {
        for (let i = 0; i < order.length; i++) {
          if (view === 'master') await supabase.from('prebuild_master').update({ sort: i + 1 }).eq('id', order[i].id);
          else await supabase.from('decisions').update({ prebuild_sort: i + 1 }).eq('id', order[i].id);
        }
        view === 'master' ? loadMaster() : onUpdate();
      }
    }
    setDragId(null);
  };

  const groupByCategory = async () => {
    const rank = (c) => { const i = CATEGORIES.indexOf(c); return i === -1 ? 999 : i; };
    const sorted = [...order].sort((a, b) => rank(a.category) - rank(b.category));
    for (let i = 0; i < sorted.length; i++) {
      if (view === 'master') await supabase.from('prebuild_master').update({ sort: i + 1 }).eq('id', sorted[i].id);
      else await supabase.from('decisions').update({ prebuild_sort: i + 1 }).eq('id', sorted[i].id);
    }
    view === 'master' ? loadMaster() : onUpdate();
  };

  // ---- form helpers ----
  const addOpt = () => { if (optList.length < 5) { setOptList([...optList, '']); setCostList([...costList, '']); } };
  const removeOpt = (i) => { if (optList.length > 2) { setOptList(optList.filter((_, idx) => idx !== i)); setCostList(costList.filter((_, idx) => idx !== i)); } };
  const updateOpt = (i, val) => setOptList(optList.map((o, idx) => idx === i ? val : o));
  const [attachList, setAttachList] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const [dragHover, setDragHover] = useState(false);

  const uploadAttachment = async (file) => {
    if (!file) return;
    setAttaching(true);
    try {
      const path = `files/prebuild_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error } = await supabase.storage.from('purchase-photos').upload(path, file);
      if (!error) {
        const { data } = supabase.storage.from('purchase-photos').getPublicUrl(path);
        setAttachList(prev => [...prev, { name: file.name, url: data.publicUrl }]);
      }
    } catch(e) { console.error('Attachment upload failed:', e); }
    setAttaching(false);
  };

  useEffect(() => {
    if (!adding) return;
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
            uploadAttachment(new File([file], `pasted_photo_${Date.now()}.${ext}`, { type: file.type }));
          }
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [adding]);

  const resetForm = () => { setItem(''); setCategory(''); setAnswerType('options'); setOptList(['', '']); setCostList(['', '']); setComments(''); setFollowUps({}); setEditingId(null); setAttachList([]); };
  const validOpts = () => optList.filter(o => o.trim());
  const canSave = item.trim() && (answerType === 'text' || validOpts().length >= 2);
  const buildCosts = (opts) => {
    const m = {};
    optList.forEach((o, i) => {
      const v = parseFloat((costList[i] || '').toString().replace(/[^0-9.]/g, ''));
      if (o.trim() && !isNaN(v) && v > 0) m[o.trim()] = v;
    });
    return Object.keys(m).length ? m : null;
  };

  const childrenOf = (d) => view === 'master'
    ? master.filter(m => m.parent_master_id === d.id)
    : allPrebuild.filter(x => x.parent_decision_id === d.id);

  const openEdit = (d) => {
    setEditingId(d.id);
    setItem(d.item || '');
    setCategory(d.category && CATEGORIES.includes(d.category) ? d.category : '');
    const hasOpts = Array.isArray(d.options) && d.options.length > 0;
    setAnswerType(hasOpts ? 'options' : 'text');
    setOptList(hasOpts ? [...d.options] : ['', '']);
    setCostList(hasOpts ? d.options.map(o => (d.option_costs && d.option_costs[o] != null) ? String(d.option_costs[o]) : '') : ['', '']);
    setComments(d.notes || '');
    setAttachList(Array.isArray(d.attachments) ? [...d.attachments] : []);
    const fu = {};
    if (hasOpts) {
      childrenOf(d).forEach(ch => {
        const oi = d.options.indexOf(ch.parent_option);
        if (oi >= 0) fu[oi] = { name: ch.item, type: Array.isArray(ch.options) && ch.options.length ? 'options' : 'text', opts: Array.isArray(ch.options) && ch.options.length ? [...ch.options] : ['', ''], _childId: ch.id };
      });
    }
    setFollowUps(fu);
    setAdding(true);
  };

  const reconcileChildren = async (parentId, opts) => {
    const table = view === 'master' ? 'prebuild_master' : 'decisions';
    const parentKey = view === 'master' ? 'parent_master_id' : 'parent_decision_id';
    const existing = view === 'master'
      ? master.filter(m => m.parent_master_id === parentId)
      : allPrebuild.filter(x => x.parent_decision_id === parentId);
    const wantedByOption = {};
    for (const [iStr, fu] of Object.entries(followUps)) {
      const optVal = opts[parseInt(iStr)];
      if (optVal && fu.name?.trim()) wantedByOption[optVal] = fu;
    }
    for (const ch of existing) {
      const want = wantedByOption[ch.parent_option];
      const answered = view === 'build' && (ch.draft_answer || ch.status !== 'pending');
      if (!want) {
        if (!answered) await supabase.from(table).delete().eq('id', ch.id);
      } else {
        if (!answered) {
          await supabase.from(table).update({
            item: want.name.trim(),
            options: want.type === 'options' ? want.opts.filter(o => o.trim()) : null,
          }).eq('id', ch.id);
        }
        delete wantedByOption[ch.parent_option];
      }
    }
    for (const [optVal, fu] of Object.entries(wantedByOption)) {
      const row = {
        item: fu.name.trim(),
        category: category || null,
        options: fu.type === 'options' ? fu.opts.filter(o => o.trim()) : null,
        [parentKey]: parentId,
        parent_option: optVal,
      };
      if (view === 'master') { row.sort = 999; }
      else { row.project_id = parseInt(projectId); row.status = 'pending'; row.is_prebuild = true; row.prebuild_sort = 999; row.category = category || 'Pre-Build'; }
      await supabase.from(table).insert(row);
    }
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    const opts = validOpts();
    if (editingId) {
      const table = view === 'master' ? 'prebuild_master' : 'decisions';
      await supabase.from(table).update({
        item: item.trim(),
        category: category || (view === 'master' ? null : 'Pre-Build'),
        notes: comments.trim() || null,
        options: answerType === 'options' ? opts : null,
        option_costs: answerType === 'options' ? buildCosts(opts) : null,
        attachments: attachList.length ? attachList : null,
      }).eq('id', editingId);
      if (answerType === 'options') await reconcileChildren(editingId, opts);
    } else if (view === 'master') {
      const maxSort = master.reduce((m, d) => Math.max(m, d.sort || 0), 0);
      const { data: parent } = await supabase.from('prebuild_master').insert({
        item: item.trim(), category: category || null, notes: comments.trim() || null,
        options: answerType === 'options' ? opts : null, option_costs: answerType === 'options' ? buildCosts(opts) : null,
        attachments: attachList.length ? attachList : null, sort: maxSort + 1,
      }).select().single();
      if (parent && answerType === 'options') {
        for (const [iStr, fu] of Object.entries(followUps)) {
          const optVal = opts[parseInt(iStr)];
          if (!optVal || !fu.name?.trim()) continue;
          await supabase.from('prebuild_master').insert({
            item: fu.name.trim(), category: category || null,
            options: fu.type === 'options' ? fu.opts.filter(o => o.trim()) : null,
            sort: maxSort + 1, parent_master_id: parent.id, parent_option: optVal,
          });
        }
      }
    } else {
      const maxSort = decisions.reduce((m, d) => Math.max(m, d.prebuild_sort || 0), 0);
      const { data: parent } = await supabase.from('decisions').insert({
        project_id: parseInt(projectId), item: item.trim(), category: category || 'Pre-Build',
        notes: comments.trim() || null, status: 'pending', is_prebuild: true,
        prebuild_sort: maxSort + 1, options: answerType === 'options' ? opts : null,
        option_costs: answerType === 'options' ? buildCosts(opts) : null,
        attachments: attachList.length ? attachList : null,
      }).select().single();
      if (parent && answerType === 'options') {
        for (const [iStr, fu] of Object.entries(followUps)) {
          const optVal = opts[parseInt(iStr)];
          if (!optVal || !fu.name?.trim()) continue;
          await supabase.from('decisions').insert({
            project_id: parseInt(projectId), item: fu.name.trim(), category: category || 'Pre-Build',
            status: 'pending', is_prebuild: true, prebuild_sort: maxSort + 1,
            options: fu.type === 'options' ? fu.opts.filter(o => o.trim()) : null,
            parent_decision_id: parent.id, parent_option: optVal,
          });
        }
      }
    }
    resetForm(); setSaving(false); setAdding(false);
    view === 'master' ? loadMaster() : onUpdate();
    if (view === 'build') loadMaster();
  };

  const remove = async (d) => {
    const table = view === 'master' ? 'prebuild_master' : 'decisions';
    for (const ch of childrenOf(d)) await supabase.from(table).delete().eq('id', ch.id);
    await supabase.from(table).delete().eq('id', d.id);
    view === 'master' ? loadMaster() : onUpdate();
  };

  const loadFromMaster = async () => {
    setLoadingMaster(true);
    const { data: existing } = await supabase.from('decisions').select('id, master_id, draft_answer, status')
      .eq('project_id', parseInt(projectId)).eq('is_prebuild', true);
    const have = new Set((existing || []).filter(e => e.master_id).map(e => e.master_id));
    let sort = decisions.reduce((m, d) => Math.max(m, d.prebuild_sort || 0), 0);
    const idMap = {};
    const tops = master.filter(m => !m.parent_master_id);
    const kids = master.filter(m => m.parent_master_id);
    for (const m of tops) {
      if (have.has(m.id)) {
        const row = (existing || []).find(e => e.master_id === m.id);
        if (row) {
          idMap[m.id] = row.id;
          if (row.status === 'pending' && !row.draft_answer) {
            // wording/options refresh from master — but never touch this build's own pricing
            await supabase.from('decisions').update({ item: m.item, options: m.options, notes: m.notes || null, attachments: m.attachments || null }).eq('id', row.id);
          }
        }
        continue;
      }
      sort += 1;
      const { data: ins } = await supabase.from('decisions').insert({
        project_id: parseInt(projectId), item: m.item, notes: m.notes || null,
        category: m.category || 'Pre-Build', status: 'pending', is_prebuild: true,
        prebuild_sort: sort, options: m.options, option_costs: m.option_costs || null,
        attachments: m.attachments || null, master_id: m.id,
      }).select().single();
      if (ins) idMap[m.id] = ins.id;
    }
    for (const m of kids) {
      if (have.has(m.id) || !idMap[m.parent_master_id]) continue;
      await supabase.from('decisions').insert({
        project_id: parseInt(projectId), item: m.item, category: m.category || 'Pre-Build',
        status: 'pending', is_prebuild: true, prebuild_sort: sort, options: m.options,
        master_id: m.id, parent_decision_id: idMap[m.parent_master_id], parent_option: m.parent_option,
      });
    }
    setLoadingMaster(false);
    onUpdate();
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[{ v: 'build', label: 'This Build' }, { v: 'master', label: '⭐ Master List' }].map(opt => (
          <button key={opt.v} onClick={() => { setView(opt.v); setAdding(false); resetForm(); }}
            style={{ flex: 1, background: view === opt.v ? 'rgba(248,218,107,0.1)' : 'transparent', border: `1px solid ${view === opt.v ? B.gold : B.slate}`, borderRadius: 9, padding: '9px', cursor: 'pointer', color: view === opt.v ? B.goldlt : B.muted, fontSize: 12, fontWeight: 700, fontFamily: FONT }}>
            {opt.label}
          </button>
        ))}
      </div>

      <div style={{ color: B.muted, fontSize: 11, lineHeight: 1.6, marginBottom: 12 }}>
        {view === 'master'
          ? 'Your standard question set — edit once, load into any build. Tap a question to edit it; hold ⠿ to drag.'
          : "This build's survey. Tap a question to edit it; hold ⠿ to drag into the order clients see."}
      </div>

      {view === 'build' && locked !== null && (
        <button onClick={toggleLock}
          style={{ width: '100%', background: locked ? 'rgba(230,126,34,0.1)' : 'transparent', border: `1px solid ${locked ? '#E67E22' : B.slate}`, borderRadius: 11, padding: 11, cursor: 'pointer', color: locked ? '#E67E22' : B.muted, fontSize: 12, fontWeight: 700, fontFamily: FONT, marginBottom: 10 }}>
          {locked ? '🔒 Locked — clients can view but not edit (tap to unlock)' : '🔓 Unlocked — clients can answer & edit (tap to lock)'}
        </button>
      )}

      {view === 'build' && master.filter(m => !m.parent_master_id).length > 0 && (
        <button onClick={loadFromMaster} disabled={loadingMaster}
          style={{ width: '100%', background: 'rgba(79,163,224,0.08)', border: '1px solid #4FA3E0', borderRadius: 11, padding: 11, cursor: 'pointer', color: '#4FA3E0', fontSize: 12, fontWeight: 700, fontFamily: FONT, marginBottom: 10 }}>
          {loadingMaster ? 'Loading…' : `⭐ Load Master List (${master.filter(m => !m.parent_master_id).length} questions)`}
        </button>
      )}

      {order.length > 1 && !adding && (
        <button onClick={groupByCategory}
          style={{ width: '100%', background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 11, padding: 10, cursor: 'pointer', color: B.muted, fontSize: 12, fontWeight: 700, fontFamily: FONT, marginBottom: 10 }}>
          ⚡ Group by Category
        </button>
      )}

      {!adding ? (
        <button onClick={() => { resetForm(); setAdding(true); }}
          style={{ width: '100%', background: 'rgba(248,218,107,0.08)', border: `1px dashed ${B.gold}`, borderRadius: 11, padding: 12, cursor: 'pointer', color: B.goldlt, fontSize: 13, fontWeight: 700, fontFamily: FONT, marginBottom: 14 }}>
          + Add {view === 'master' ? 'Master ' : ''}Question
        </button>
      ) : (
        <div style={{ background: B.navy, borderRadius: 12, padding: 16, marginBottom: 14, border: `1px solid ${B.gold}` }}>
          <div style={{ color: B.goldlt, fontSize: 12, fontWeight: 800, marginBottom: 12 }}>{editingId ? '✎ Edit Question' : '+ New Question'}</div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>Decision Name *</div>
            <input value={item} onChange={e => setItem(e.target.value)} autoFocus
              placeholder="e.g. Master bath tile selection"
              style={{ width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '10px 12px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box' }} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>Category</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {CATEGORIES.map(c => (
                <button key={c} onClick={() => setCategory(category === c ? '' : c)}
                  style={{ background: category === c ? B.goldlt : B.mid, color: category === c ? B.black : B.light, border: `1px solid ${category === c ? B.goldlt : B.slate}`, borderRadius: 20, padding: '6px 13px', cursor: 'pointer', fontSize: 11.5, fontWeight: category === c ? 700 : 400, fontFamily: FONT }}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>Answer Type *</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[{ val: 'options', label: '📋 Options', sub: 'List 2–5 choices' }, { val: 'text', label: '✏️ Text Box', sub: 'Free-form answer' }].map(t => (
                <button key={t.val} onClick={() => setAnswerType(t.val)}
                  style={{ background: answerType === t.val ? 'rgba(154,115,47,0.2)' : B.mid, border: `2px solid ${answerType === t.val ? B.goldlt : B.slate}`, borderRadius: 12, padding: '13px 10px', cursor: 'pointer', fontFamily: FONT, textAlign: 'center' }}>
                  <div style={{ color: answerType === t.val ? B.goldlt : B.white, fontSize: 13, fontWeight: 700, marginBottom: 3 }}>{t.label}</div>
                  <div style={{ color: B.muted, fontSize: 10.5 }}>{t.sub}</div>
                </button>
              ))}
            </div>
          </div>

          {answerType === 'options' && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>Options (2–5)</div>
              {optList.map((opt, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input placeholder={`Option ${i + 1}`} value={opt} onChange={e => updateOpt(i, e.target.value)}
                      style={{ flex: 1, background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '10px 12px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box' }} />
                    <input placeholder="$ approx" value={costList[i] || ''} inputMode="decimal"
                      onChange={e => setCostList(costList.map((x, xi) => xi === i ? e.target.value : x))}
                      style={{ width: 86, background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '10px 8px', color: B.goldlt, fontSize: 12, outline: 'none', fontFamily: FONT, boxSizing: 'border-box' }} />
                    <button onClick={() => followUps[i] ? removeFU(i) : setFU(i, {})}
                      title="If they pick this, ask a follow-up"
                      style={{ background: followUps[i] ? 'rgba(79,163,224,0.15)' : B.mid, border: `1px solid ${followUps[i] ? '#4FA3E0' : B.slate}`, borderRadius: 8, padding: '0 10px', cursor: 'pointer', color: followUps[i] ? '#4FA3E0' : B.muted, fontSize: 13, fontWeight: 700 }}>↳</button>
                    {optList.length > 2 && (
                      <button onClick={() => { removeOpt(i); removeFU(i); }} style={{ background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 8, padding: '0 12px', cursor: 'pointer', color: B.muted, fontSize: 16 }}>×</button>
                    )}
                  </div>
                  {followUps[i] && (
                    <div style={{ marginLeft: 16, marginTop: 8, padding: 12, background: 'rgba(79,163,224,0.05)', border: '1px solid #2A4258', borderRadius: 10 }}>
                      <div style={{ color: '#4FA3E0', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>↳ If they pick "{opt || `option ${i + 1}`}", then ask (required):</div>
                      <input placeholder="Follow-up question" value={followUps[i].name || ''} onChange={e => setFU(i, { name: e.target.value })}
                        style={{ width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 8, padding: '9px 11px', color: B.white, fontSize: 12.5, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', marginBottom: 8 }} />
                      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                        {[{ v: 'options', l: '📋 Options' }, { v: 'text', l: '✏️ Text' }].map(t => (
                          <button key={t.v} onClick={() => setFU(i, { type: t.v })}
                            style={{ flex: 1, background: (followUps[i].type || 'text') === t.v ? 'rgba(154,115,47,0.2)' : B.mid, border: `1px solid ${(followUps[i].type || 'text') === t.v ? B.goldlt : B.slate}`, borderRadius: 8, padding: 8, cursor: 'pointer', color: (followUps[i].type || 'text') === t.v ? B.goldlt : B.muted, fontSize: 11.5, fontWeight: 700, fontFamily: FONT }}>
                            {t.l}
                          </button>
                        ))}
                      </div>
                      {(followUps[i].type || 'text') === 'options' && (followUps[i].opts || ['', '']).map((fo, fi) => (
                        <input key={fi} placeholder={`Choice ${fi + 1}`} value={fo}
                          onChange={e => setFU(i, { opts: (followUps[i].opts || ['', '']).map((x, xi) => xi === fi ? e.target.value : x) })}
                          style={{ width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 8, padding: '8px 11px', color: B.white, fontSize: 12, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', marginBottom: 6 }} />
                      ))}
                      {(followUps[i].type || 'text') === 'options' && (followUps[i].opts || []).length < 5 && (
                        <button onClick={() => setFU(i, { opts: [...(followUps[i].opts || ['', '']), ''] })}
                          style={{ background: 'transparent', border: `1px dashed ${B.slate}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: B.muted, fontSize: 11, fontFamily: FONT, width: '100%' }}>+ Choice</button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {optList.length < 5 && (
                <button onClick={addOpt} style={{ background: 'transparent', border: `1px dashed ${B.slate}`, borderRadius: 9, padding: '8px 16px', cursor: 'pointer', color: B.muted, fontSize: 12, fontFamily: FONT, width: '100%' }}>+ Add Option</button>
              )}
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <div style={{ color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>Comments</div>
            <textarea value={comments} onChange={e => setComments(e.target.value)} rows={2}
              placeholder="Notes, context clients will see…"
              style={{ width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '10px 12px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5 }} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>📎 Attachments{attachList.length > 0 ? ` (${attachList.length})` : ''}</div>
            {attachList.map((a, i) => {
              const isImg = /\.(png|jpe?g|gif|webp|heic)($|\?)/i.test(a.url) || /\.(png|jpe?g|gif|webp|heic)$/i.test(a.name || '');
              return (
                <div key={i} style={{ background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '8px 11px', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13 }}>{isImg ? '🖼' : '📎'}</span>
                    <a href={a.url} target="_blank" rel="noreferrer"
                      style={{ flex: 1, color: '#4FA3E0', fontSize: 12, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</a>
                    <button onClick={() => setAttachList(prev => prev.filter((_, xi) => xi !== i))}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: B.muted, fontSize: 14, padding: 0 }}>×</button>
                  </div>
                  {isImg && (
                    <a href={a.url} target="_blank" rel="noreferrer">
                      <img src={a.url} alt="" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 8, marginTop: 8, display: 'block' }} />
                    </a>
                  )}
                </div>
              );
            })}
            <label
              onDragOver={e => { e.preventDefault(); setDragHover(true); }}
              onDragLeave={() => setDragHover(false)}
              onDrop={e => { e.preventDefault(); setDragHover(false); Array.from(e.dataTransfer.files || []).forEach(fl => uploadAttachment(fl)); }}
              style={{ display: 'block', border: `1px dashed ${dragHover ? B.goldlt : B.slate}`, background: dragHover ? 'rgba(248,218,107,0.06)' : 'transparent', borderRadius: 10, padding: '9px 14px', cursor: 'pointer', color: dragHover ? B.goldlt : B.muted, fontSize: 12, textAlign: 'center' }}>
              📎 {attaching ? 'Uploading…' : 'Attach a file — drag & drop, paste, or tap'}
              <input type="file" multiple style={{ display: 'none' }}
                onChange={e => { Array.from(e.target.files || []).forEach(fl => uploadAttachment(fl)); e.target.value = ''; }} />
            </label>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={save} disabled={saving || !canSave}
              style={{ flex: 1, background: canSave ? B.goldlt : B.slate, color: canSave ? B.black : B.muted, border: 'none', borderRadius: 9, padding: 12, cursor: canSave ? 'pointer' : 'default', fontSize: 13, fontWeight: 800, fontFamily: FONT }}>
              {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Save Question'}
            </button>
            <button onClick={() => { resetForm(); setAdding(false); }}
              style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 9, padding: '12px 16px', cursor: 'pointer', color: B.muted, fontSize: 13, fontFamily: FONT }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {order.length === 0 && !adding && (
        <div style={{ background: B.navy, borderRadius: 14, padding: 30, textAlign: 'center', border: `1px solid ${B.slate}` }}>
          <div style={{ fontSize: 30, marginBottom: 8 }}>📋</div>
          <div style={{ color: B.muted, fontSize: 12 }}>{view === 'master' ? 'No master questions yet — build your standard set once.' : 'No pre-build questions on this build yet.'}</div>
        </div>
      )}

      <div
        onTouchMove={dragMove} onTouchEnd={dragEnd} onTouchCancel={dragEnd}
        onMouseMove={dragId ? dragMove : undefined} onMouseUp={dragId ? dragEnd : undefined} onMouseLeave={dragId ? dragEnd : undefined}>
        {order.map((d, i) => (
          <div key={d.id}>
          {(i === 0 || (order[i - 1].category || '') !== (d.category || '')) && !dragId && (
            <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', margin: i === 0 ? '4px 0 8px' : '14px 0 8px' }}>
              {d.category || 'Uncategorized'}
            </div>
          )}
          <div ref={el => rowRefs.current[d.id] = el}
            style={{ background: B.navy, borderRadius: 12, padding: '12px 14px', marginBottom: 6, border: `1px solid ${dragId === d.id ? B.goldlt : B.slate}`, display: 'flex', alignItems: 'center', gap: 10, opacity: dragId === d.id ? 0.5 : 1 }}>
            <div
              onTouchStart={dragStart(d.id)} onMouseDown={dragStart(d.id)}
              style={{ color: B.muted, fontSize: 16, cursor: 'grab', padding: '4px 2px', touchAction: 'none', userSelect: 'none' }}>⠿</div>
            <div style={{ color: B.muted, fontSize: 11, fontWeight: 800, minWidth: 20 }}>{i + 1}.</div>
            <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => openEdit(d)}>
              <div style={{ color: B.white, fontSize: 13, fontWeight: 600 }}>
                {d.item}
                {view === 'build' && d.master_id && <span style={{ color: '#4FA3E0', fontSize: 9, marginLeft: 6 }}>⭐</span>}
                {childrenOf(d).length > 0 && <span style={{ color: '#4FA3E0', fontSize: 9, marginLeft: 6 }}>↳ follow-up</span>}
              </div>
              {view === 'build' && d.draft_answer && <div style={{ color: '#4FA3E0', fontSize: 11, marginTop: 2 }}>Draft: {d.draft_answer}</div>}
              <div style={{ color: '#555', fontSize: 10, marginTop: 2 }}>
                {d.category ? `${d.category} · ` : ''}{Array.isArray(d.options) && d.options.length > 0 ? d.options.map(o => d.option_costs && d.option_costs[o] ? `${o} (~$${Number(d.option_costs[o]).toLocaleString()})` : o).join(' · ') : 'Text answer'}
              </div>
            </div>
            <button onClick={() => remove(d)}
              style={{ background: 'transparent', border: '1px solid #4A2A2A', borderRadius: 7, padding: '5px 9px', cursor: 'pointer', color: '#E74C3C', fontSize: 11 }}>×</button>
          </div>
          </div>
        ))}
      </div>
    </div>
  );
}
